/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {Moment} from 'moment';
import {Results} from '../services/GroupProperties';
import {UserContext} from '../services/MicroServices';
import {DateType} from '../utils/DateUtil';
import {Operation} from './general';
import {
  AccumType,
  APIGatewayMemberSearch,
  APIGatewaySearch,
  APIGatewaySmartSearch,
  HierLevel,
  RssiType
} from './services';

/* eslint-disable no-multi-spaces */
/* eslint-disable @typescript-eslint/type-annotation-spacing */

// defines a generic object type which can be used when an objects properties
// are not all known at design time ...
export interface GenericObject {
  [key: string]: any;
}

/**
 * Defines a type alias for providing an `Entity` name based on the either
 * an `Entity` enum value or a string literal.
 */
export type EntityName = Entity | string;

/**
 * Defines a type alias for providing an `Operation` name based on the either
 * an `Operation` enum value or a string literal.
 */
export type EntityOperation = Operation | string

/**
 *
 * CTCEntity Bulk search responses wrapped with Entity name.
 * Need to pass Entity type as param with CTCEntityResponseWrapper type.
 * For example: CTCEntityResponseWrapper<Schedules>, CTCEntityResponseWrapper<Devices>
 *
 */
export interface CTCEntityResponseWrapper<EntityType> {
  [key : string] : EntityType;
}

// defines the device move type
export interface DeviceMove {
  selectedAccount: Account | Account[]
};

// defines the CTC HATEOAS Link type ...
export interface Link {
  href: string;
  rel?: string;
  status?: EntityStatus;
  title?: string;
  source?: Link;
};
export type Links = Link[];

// defines the base entity for ctc based entities ...
export interface CTCEntity {
  id?: number;
  version?: number;
  status?: EntityStatus;
  account?: Link;
  accountId?: number;
  _self?: Link;
  name?: string;
  context?: UserContext;
  // the enriched property allows for persisting temporary properties on an
  // entity which can be subsequently deleted; this is particularly useful
  // for when an entity is retrieved from another service, such as graphql,
  // and the entity needs to be cast to a CTC type; however, subsequent services
  // may have previously been required for retrieving the fully qualified entities
  // in place of the entities links; such as an entity's account, roles, etc. the
  // generic enriched property allows for temporarily storing these values and
  // using them in place of making additional service calls, after which, as a
  // best practice, the enriched property should be nullified or deleted.
  enriched?: GenericObject
};
export type CTCEntities = CTCEntity[];

export interface SubAccountLink {
  subaccount?: Link;
  href: string
}

export interface AccountLink extends Link {
  subAccounts?: SubAccountLink[]
}

export interface Crumbs {
  subAccounts?: Link;
  href: string
  accountId: number;
  path: string[];
  title: string;
  status: EntityStatus;
}

export type Unit = 'Default' | 'US' | 'Imperial' | 'Metric' | 'SI' | 'Mixed' | 'Device';

export const MaxEmailLimit = 25;
export const AccountErrorMessageIndex = 23;

// provides a union of the various account type representations
export type AccountUnion = Link | Account | number;

export type ExtendedAttribute = {
  value?: string | GenericObject;
  lastModifiedOn?: DateType;
  createdOn?: DateType;
  application?: Link
};

export interface Permission extends CTCEntity {
  resource?: string;
  action?: string;
  additionalPermissions?: Permission[] | unknown;
  groups?: Link[];
  description?: string;
  expanded?: unknown;
};
export type Permissions = Permission[];

export type PermissionResult = {
  permission?: Permission
};

export type PermissionResults = PermissionResult[];

export interface Role extends CTCEntity {
  name?: string;
  inheritable?: boolean;
  application?: Link;
  permissions?: Link[];
  groups?: Link[]
  extendedAttribute?: ExtendedAttribute;
  description?: string;
  activationDate?: DateType;
  deletionDate?: string;
  expanded?: unknown;
};
export type Roles = Role[];

export type RoleResult = {
  role?: Role
};

export type AddressType = {
  city?: string
  country?: string
  countryCd?: string
  stateProvince?: string
  stateProvinceCd?: string
  street?: string
  zip?: string
};

export interface LastAvlCommunicationType {
  address?: AddressType;
  eventCode?: string;
  eventLabel?: string;
  fixStatus: boolean;
  locationTime?: string;
  latitude?: number;
  longitude?: number;
}

export interface FileHistoryPendingType{
  jobId?: number;
  jobStatus?: string;
  newVersion?: number;
  platformId?: number;
}

export interface Application extends CTCEntity {
  applicationKey?: string;
  name?: string;
  description?: string;
  extendedAttribute?: ExtendedAttribute;
  domains?: string[],
  status?: EntityStatus;
  groups?: unknown;
  lastActivatedOn?: string;
  subscriptionOnly?: boolean;
  deletedOn?: null;
};
export type Applications = Application[];

export enum PegLabelType {
  ACCUMULATOR = 'ACCUMULATOR',
  EVENT       = 'EVENT'
};

export interface PegLabel extends CTCEntity {
  conversionFactor?: string;
  createdOn?: string;
  description?: string;
  expanded?: GenericObject;
  groups?: Links;
  inherited?: boolean;
  lastModifiedOn?: string;
  signed?: boolean;
  systemDefined?: boolean;
  type?: PegLabelType;
  unit?: string;
  title?: string;
};
export type PegLabels = PegLabel[];

export interface HealthEvent extends CTCEntity {
  eventType?: string;
  triggerReason?: string;
  updatedIdReport?: number;
  diffCount?: number;
};
export type HealthEvents = HealthEvent[];

export interface HardwarePart extends CTCEntity {
  id?: number;
  partNumber: any;
  type?: string;
  description: string;
  platform?: number;
  application?: string;
  state?: string;
  defaultPrice: string;
  topLevelChildConfigurationMapping: string;
  provisioningAccount: string;
  carrier: string;
  deviceTypeName: string;
  deviceConfigurationName: string;
  enableOtaspVerification: boolean;
  deviceCreationJob?: DeviceCreationJob;
};
export type HardwareParts = HardwarePart[];

export interface DeviceCreationJob {
  state?: string ;
  deviceIdentifierType?: string;
  provision?: string;
  provisioningType?: string;
  createDevices?: boolean;
  deviceSource?: number;
  queryParams?: Object;
}

export interface HardwarePartsProps {
  onChange?: Function;
  defaultValue?: string;
  fieldLabel?: string;
  placeholder?: string;
  errorText?: string;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  value?: string;
  onSuccess?: () => void;
}

export enum HardwarePartType {
  Applications = 'applications',
  States = 'states',
  Platforms = 'platforms',
  Carriers = 'carriers',
  DeviceSource = 'deviceSource'
};

export interface PegBehavior extends CTCEntity {
  id: number;
  createdOn?: string;
  description?: string;
  groups?: Links;
  inherited: boolean;
  lastModifiedOn?: string;
  name?: string;
  pegEvents?: PegEvents;
  scriptDetails?: string;
  status?: EntityStatus;
};
export type PegBehaviors = PegBehavior[];

export interface PegBehaviorIncludes extends CTCEntity {
  pegEvents: string[];
  pegAccumulators: string[];
};

export interface PegEvent extends CTCEntity {
  pegEvent?: PegEvent;
  cost?: number;
  eventCode?: number;
  lastModifiedOn?: string;
  messageType?: string;
  pegAccumulators?: PegAccumulators;
  pegBehavior?: PegBehavior;
  pegLabel?: PegLabel;
  isModifiedEvent?: boolean;
}
export type PegEvents = PegEvent[];

export interface PegAccumulator extends CTCEntity {
  pegAccumulator?: GenericObject;
  accumulatorFormat?: string;
  conversionFactor?: string;
  createdOn?: DateType;
  description?: string;
  index?: number;
  lastModifiedOn?: DateType;
  pegEvent?: PegEvent;
  pegLabel?: PegLabel;
  souQuantity?: string;
  units?: string;

}
export type PegAccumulators = PegAccumulator[];

export enum AccumFieldsName {
  PEG_LABEL = 'pegLabel',
  SOU_QUANTITY = 'souQuantity',
  ACCUMULATOR_FORMAT = 'accumulatorFormat',
  CONVERSION_FACTOR = 'conversionFactor'
};

export interface ClonedPegBehavior extends CTCEntity {
  name?: string;
  destination?: AccountLink;
  inheritable?: boolean;
  description?: string;
};
export type ClonedPegBehaviors = ClonedPegBehavior[];

export interface systemOfUnit extends CTCEntity {
  conversionFactor?: string;
  description?: string;
  dimensions?: string;
  id?: number;
  name?: string;
  unit?: string;
  label?: string;
};
export type systemOfUnits = systemOfUnit[];

export interface ModuleAction {
  id: number;
  name: string;
  enabled: boolean;
};
export type ModuleActions = ModuleAction[];

export interface AlertNotification extends CTCEntity {
  accountName?: string;
  accountTimeZone?: any;
  acknowledgedBy?: any;
  acknowledgedById?: string | number;
  acknowledgedOn?: any;
  acknowledgedUserName?: string;
  address?: any;
  alertAction?: GenericObject;
  alertConfiguration?: GenericObject;
  alertConfigurationId?: number;
  alertConfigurationName?: string;
  alertTriggers?: GenericObject[] | GenericObject;
  alertType?: GenericObject;
  alertTypeName?: string;
  assetName?: string;
  createdOn?: string;
  device?: Link;
  deviceIdentifier?: string;
};
export type AlertNotifications = AlertNotification[];

export enum EntityAssetType {
  fleet = 'Fleet',
  cargo = 'Cargo'
};

export enum DeviceFileStatus {
  VALID = 'VALID',
  INVALID = 'INVALID'
};

export interface BaseSearchFilters {
  name?: string | null;
}

export interface StatusBasedSearchFilters extends BaseSearchFilters {
  status?: string | null;
}

export interface ProgressStatusBasedSearchFilter extends BaseSearchFilters {
  progressStatus?: string | null;
}

export interface AdminAEMPConfigFilters extends StatusBasedSearchFilters {}

export interface ApplicationFilters extends StatusBasedSearchFilters {
  applicationKey?: string | null;
}

export interface AssetsFilters extends StatusBasedSearchFilters {
  assetType?: AssetType.Asset | AssetType.Vehicle | AssetType.Tool;
  licenseNumber?: string;
  vin?: string;
  groups?: number;
}

export interface DownloadJobsFilters extends ProgressStatusBasedSearchFilter {
  id?: number;
  createdOn?: string;
}

export interface DevicesFilters extends StatusBasedSearchFilters {
  any?: string;
  id?: number;
  airId?: string;
  esn?: string;
  iccid?: string;
  imei?: string;
  meid?: string;
  serialNumber?: string;
  appId?: string;
  typeId?: number;
  category?: string;
  firmwareVersion?: string;
  configVersion?: string;
  idReportTimestamp?: string;
  lastReportedTime?: string;
  groupIds?: number;
  architecture?: string;
}

export interface DeviceFileFilters extends BaseSearchFilters {
  platformId?: string;
  version?: string;
  status?: DeviceFileStatus;
  appId?: string;
}

export interface UserFilters extends StatusBasedSearchFilters {
  username?: string;
  firstName?: string;
  lastName?: string;
}

export interface PegBehaviorsFilters extends StatusBasedSearchFilters {}

export interface PegLabelsFilters extends BaseSearchFilters {
  type?: PegLabelType
}

export interface NotificationTypeFilters extends StatusBasedSearchFilters {}

export interface AdminGroupsFilters extends StatusBasedSearchFilters {
  id?: string;
  solomonId?: string;
}

export interface AdminRolesFilters extends StatusBasedSearchFilters {
  application?: string | null;
}

export interface AdminNotificationsFilters extends StatusBasedSearchFilters {
  severity?: AlertSeverity.CRITICAL | AlertSeverity.HIGH | AlertSeverity.LOW
  | AlertSeverity.MEDIUM | AlertSeverity.SYSTEM;
}

export interface NotificationsFilters {
  severity?: AlertSeverity.CRITICAL | AlertSeverity.HIGH | AlertSeverity.LOW
  | AlertSeverity.MEDIUM | AlertSeverity.SYSTEM;
  alertTypeName?: string;
  esn?: number;
  alertConfigurationName?: string;
  createdOn?: string;
  assetName?: string;
}

export interface GroupsFilters extends BaseSearchFilters {};

export interface DeviceTypesFilters extends BaseSearchFilters {}

export interface DeviceConfigsFilters extends BaseSearchFilters {
  deviceType?: number | null;
  pegBehavior?: number | null;
}

export interface ApplicationIdsFilters {
  manufacturer: string;
  model: string;
  microprocessor: string;
  cellularModem: string;
  deviceFamily: string;
  carrier: string;
  status: string;
  createdOn: string;
};

export interface ShipmentsFilters {
  trackingNum: string,
  shipperId: string,
  salesOrderNum: string,
  purchaseOrderNum: string,
  ctcRegion: string,
  shipDate: string,
  createdDate: string,
};

export interface FeaturePermissions extends GenericObject {
};

export type ApplicationFeature = {
  id: string;
  name: string;
  description?: string;
  permissions?: Link[] | [];
  features?: ApplicationFeature[];
  modules?: GenericObject;
  /** Flag to determine whether the feature is enabled/disabled in UI based on
   * whether the user has permission to grant/revoke the feature
   */
  enabled?: boolean;
  /** Flag to store Allow/Deny state */
  allowed?: boolean;
  default?: boolean;
};
export type ApplicationFeatures = ApplicationFeature[];

// defines the CTC entity types ...
export enum Entity {
  account = 'account',
  appid = 'appid',
  messageEndpoint = 'messageEndpoint',
  aemppollerconfigurator = 'aempPollerConfigurator',
  airtimeplan = 'airtimeplan',
  alert = 'alert',
  alerttype = 'alertType',
  alerttrigger = 'alerttrigger',
  application = 'application',
  asset = 'asset',
  autoassetcreate = 'autoassetcreate',
  contact = 'contact',
  devicecommand = 'devicecommand',
  deviceconfiguration = 'deviceconfiguration',
  deviceresource = 'deviceresource',
  device = 'device',
  devices = 'devices',
  devicepassword = 'devicepassword',
  devicetype = 'devicetype',
  devicetypes = 'deviceType',
  deviceupdatetemplate = 'deviceupdatetemplate',
  downloadjob = 'downloadjob',
  eventcode = 'eventcode',
  file = 'file',
  filemeta = 'filemeta',
  inventorydevice = 'inventoryDevice',
  jobs = 'jobs',
  job = 'job',
  task = 'task',
  geomapper = 'geomapper',
  geozone = 'geozone',
  geofence = 'geofence',
  groupmember = 'groupmember',
  groupmembers = 'groupmembers',
  group = 'group',
  ioprofile = 'ioprofile',
  keyfile = 'keyfile',
  keyfilegenerator = 'keyfilegenerator',
  messagereplay = 'messagereplay',
  notification = 'notification',
  notifications = 'notifications',
  notificationType = 'notificationType',
  reports = 'report',
  alertConfiguration = 'alertConfiguration',
  operator = 'operator',
  pegbehavior = 'pegbehavior',
  pegbehaviors = 'pegBehavior',
  pegevent = 'pegevent',
  pegevents = 'pegevents',
  pegaccumulator = 'pegaccumulator',
  pegaccumulators = 'pegaccumulators',
  peglabelAudit = 'pegLabel',
  peglabel = 'peglabel',
  peglabels = 'peglabels',
  permission = 'permission',
  resource = 'resource',
  role = 'role',
  route = 'route',
  schedule = 'schedule',
  serviceprofile = 'serviceprofile',
  subscription = 'subscription',
  systemofunit = 'systemofunit',
  user = 'user',
  weather = 'weather',
  maintenanceservice = 'maintenance service',
  maintenanceschedule = 'maintenance schedule',
  schedulereport = 'Schedule Report',
  hardwarepart = 'hardwarepart',
  partnumber = 'partnumber',
  ssoConfiguration = 'ssoConfiguration',
  applicationsIds= 'applicationsIds',
  devicePartNumber = 'devicePartNumber',
  uploadManufacturedDevices = 'uploadManufacturedDevices',
  manufacturing = 'manufacturing',
  manufacturedDevice = 'manufacturedDevices',
  shipment = 'shipment',
  applicationId = 'applicationId',
  customerfile = 'customerfile',
  provisionedDevice = 'provisionedDevice',
  lmuscript = 'lmuscript',
  lmufirmware = 'lmufirmware',
  vbusconfig = 'vbusconfig',
  vbusfirmware = 'vbusfirmware',
  vbushostedapp = 'vbushostedapp',
  bluetoothfirmware = 'bluetoothfirmware',
  radiofirmware = 'radiofirmware',
  custompackagesedgeapp = 'custompackagesedgeapp',
  custompackageshostedapp = 'custompackageshostedapp',
  custompackagesopkg =  'custompackagesopkg',
  customerdatabasedriverid = 'customerdatabasedriverid',
  customerdatabasegeozone = 'customerdatabasegeozone',
  customerdatabaseapn = 'customerdatabaseapn',
  mdtconfig = 'mdtconfig',
  mdtfirmware =  'mdtfirmware',
  lmudigitalsignature = 'lmudigitalsignature',
  vin = 'Vin'
}

// defines the asset status enumeration ...
export enum EntityStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  Suspended = 'Suspended',
  Deleted = 'Deleted'
};

// defines the Global Search Modules ...
export enum GlobalSearchModule {
  Accounts = 'Accounts',
  Users = 'Users',
  Assets = 'Assets by Name',
  AssetsVin = 'Assets by VIN',
  Devices = 'Devices',
  Groups = 'Groups'
};

// defines the Global Search Modules ...
export enum DeviceServiceTypes {
  tagDetection = 'Tag Detection',
  videoService = 'Video Service',
  PendingEnable = 'PendingEnable',
  ionVision = 'IOn Vision',
  icn = 'ICN'
};

//defines the ICN Application keys
export enum ICNApplications {
  ICNPlus = '3953a5b7-3009-410d-b4d5-b941aeca586f',
  FirstNoticeOfLoss = '9fed2e7a-55bf-49e1-b316-626dc915507e',
  AccidentReconstruction = '645e2ab6-e087-4303-98c9-eaf79dce3efa',
  PredictivePhysicalDamage = '031b47b9-0298-43d3-b2de-49221f085d74'
};

export enum CrashboxxApplications {
  Portal = 'd151a1b2-f0bd-4bd5-b587-402a45a42c76',
  CrashboxxPortal = 'b11b624a-55cc-487c-835d-2157311b7721'
};

export enum PlatformIdTypes {
  VBUS    = 0,
  APP_IDS = 1,
  FILES   = 2
}

// defines the aempconfig PollingInterval enumeration ...
export const PollingIntervals = [1, 2, 4, 8, 12, 24, 48];

//defines the Device Service State Types
export const serviceStateType1 = ['Not supported', 'Supported', 'Activated', 'Operational'];
export const serviceStateType2 = ['Not supported', 'Supported', 'Pending', 'Operational'];

//defines the IDReport Linux Device files Status ...
export const linuxDeviceFileStatus = [
  'UNDEFINED',
  'INSTALLED',
  'FAILED',
  'FAILED INSTALLING TIMEOUT',
  'FAILED UNINSTALLING TIMEOUT',
  'FAILED REMOVING TIMEOUT',
  'INSTALLED UPDATED', 'INSTALLED FAILED',
  'UNINSTALLED',
  'UPGRADING',
  'UNINSTALLING',
  'REMOVING',
  'GOT NEW VER',
  'GOT REMOVED',
  'FAILED NO CUSTOMW',
  'INSTALLED RSVD1',
  'INSTALLED RSVD2',
  'MAX RESULT'
];

// defines the Geofence Event Types enumeration ...
export enum GeofenceEventTypes {
  Depart = 'Exit',
  Arrive = 'Enter',
  Both = 'Both (Enter & Exit)'
};

// defines the Schedule Reference Types enumeration ...
export enum ScheduleReference {
  In = 'InsideSchedule',
  Out = 'OutsideSchedule'
}

export type EntityStatuses = EntityStatus[];

// defines the asset types enumeration ...
export enum AssetType {
  Vehicle     = 'Vehicle',
  Machine     = 'Machine',
  Powered     = 'Powered',
  Unpowered   = 'Unpowered',
  Tool        = 'Tool',
  Equipment   = 'Equipment',
  Asset       = 'ASSET',
  Trailer     = 'Trailer',
  Default     = 'ASSET',
  Others      = 'Others',
  ScDevice    = 'ScDevice'
};
export type AssetTypes = AssetType[];

// defines the asset state enumeration ...
export enum AssetState {
  ACTIVE = 'Active',
  TOWED = 'Towed',
  PARKED = 'Parked',
  IDLE = 'Idle',
  MOVING = 'Moving',
  STATIONARY = 'Stationary',
  NOGPS = 'NoGPS'
}

// defines the device state enumeration ...
export enum DeviceState {
  STATIONARY = 'STATIONARY',
  MOVING = 'MOVING',
}

// Define the History Event by Asset
export enum HistoryEventByAsset {
  IGON        = 'IGON',
  IGOFF       = 'IGOFF',
  STOP        = 'STOP',
  ABATLO      = 'ABATLO',
  BBATLO      = 'BBATLO',
  VBATLO      = 'VBATLO',
  DIRECTION   = 'DIRECTION',
  INPUTCHANGE = 'INPUTCHANGE'
};

// defines the shape of a crumb object in a hook
export type Crumb = {
  label?: string;
  assetId?: number;
  click: string | undefined;
};

export enum PhoneType {
  HOME      = 'HOME',
  WORK      = 'WORK',
  CELL      = 'CELL',
  FAX       = 'FAX',
  TOLL_FREE = 'TOLL_FREE'
};

// Phone number props
export type PhoneNumber = {
  extension: any,
  phoneNumber: string,
  phoneType: PhoneType
};
export type PhoneNumbers = PhoneNumber[];

// Linux device files prop
export type LinuxDeviceFile = {
  rawIdReport: string
}

// defines the ctc user entity interface ...
export interface User extends CTCEntity {
  accountType?: string;
  activationDate?: string;
  addresses?: unknown[];
  applications?: Link[];
  authActivities?: unknown;
  createdOn?: string;
  crmId?: string;
  deletionDate?: string;
  email?: string;
  eulaAccepted?: boolean;
  extendedAttribute?: ExtendedAttribute;
  firstName?: string;
  groups?: unknown;
  lastModifiedOn?: string;
  lastName?: string;
  password?: boolean;
  passwordUpdated?: boolean;
  phoneNumbers?: unknown[];
  pin?: unknown;
  preferences?: unknown[];
  roles?: Link[];
  systemOfUnits?: { value: string, source?: unknown };
  tempPassword?: unknown;
  terminationDate?: string;
  timeZone?: string;
  unitOfMeasure?: string;
  username?: string;
  notify?: boolean;
  accountId?: number;
  isSsoUser?: boolean;
  expanded?: any;
};
export type Users = User[];

export type UserPreferencesSettings = {
  defaultGroupId?: number;
  defaultPageId?: number;
  defaultLanguage?: string;
  defaultBookmark?: string;
  persistGridLayouts?: boolean;
  persistTrackingSettings?: boolean;
  passwordConfirm?: boolean;
  iOnSuiteWelcome?: boolean;
  environment?: unknown;
}

// defines the ui specific augment ctc user entity interface, this type
// simply defines an additional settings property which is used for storing
// the current user's UserPreferencesSettings.
export interface EnhancedUser extends User {
  settings?: UserPreferencesSettings;
  defaultLanguage?: string
}
export type EnhancedUsers = EnhancedUser[];

export type UserResult = {
  user: User;
}

// Tool Status typography values
export enum ToolStatus {
  ONLINE = 'Online',
  DISCONNECTED = 'Disconnected'
}

// defines the iOn Vision Video Event type ...
export interface VideoEvent {
  readonly id: number;
  readonly appmessageUuid: string;
  readonly avlEventUuid: string;
  readonly createdOn: string;
  readonly appmsgEventTime: string;
  readonly avlEventTime: string;
  readonly eventIndex: number;
  readonly account: Link;
  readonly asset: Link;
  readonly operator: Link;
  readonly device: Link;
  readonly deviceId: string;
  readonly deviceIdType: string;
  readonly messageType: string;
  readonly videoLink: string;
}
export type VideoEvents = VideoEvent[];

// AEMPConfig type definition ...
export interface AEMPConfig extends CTCEntity {
  lastRunTime?: Date | string;
  lastRunStatus?: string;
  nextPollTime?: Date | string;
  pollingInterval?: number;
  description?: string;
  username?: string;
  password?: string;
  name?: string;
  url?: string;
  additionalUrlParams?: string;
  createdOn?: Date | string;
  lastModifiedOn?: Date | string;
};
export type AEMPConfigs = AEMPConfig[];

// Group type definition ...
export interface Group extends CTCEntity {
  name?: string;
  membersCount?: any[];
  createdOn?: Date | string;
  lastModifiedOn?: Date | string;
  username?: string;
  notes?: string;
  members?: string;
  devices?: Device[];
  assets?: Asset[];
  inheritable?: boolean;
  link?: {
    href?: string;
    title?: string;
    rel?: string
  }
};
export type Groups = Group[];

// GroupMembers type definition ...
export interface GroupMember extends CTCEntity {
  searchCriteria?: any[];
  link?: Link;
};
export type GroupMembers = GroupMember[];

export interface BaseAsset extends CVSAssetType, SCDeviceType {
  assetAlias?: null | string;
  accountId?: number;
  accountName?: string;
  accountStatus?: string;
  assetName: string;
  assetCategory?: string;
  assetId?: number;
  assetStatus?: EntityStatus;
  assetType?: AssetType;
  city?: string;
  commStateAvailable?: string; // Stringified Boolean!
  commStateConnected?: string; // Stringified Boolean!
  commStateDataService?: string; // Stringified Boolean!
  commStateNetworkService?: string; // Stringified Boolean!
  commStateRoaming?: string; // Stringified Boolean!
  commStateThreeGNetwork?: string; // Stringified Boolean!
  commStateVoiceCallActive?: string; // Stringified Boolean!
  country?: string;
  county?: string
  crossStreet?: string
  dbWriteTime?: number | string;
  deviceObjectId?: number;
  deviceEsn?: string;
  deviceId?: number;
  eventTime: number | string | DateType;
  readonly geometry?: {
    coordinates?: Array<number>;
    x: number;
    y: number;
  };
  id: number;
  isScDevice: boolean;
  isCvsAsset: boolean;
  landmark?: GenericObject;
  landmarkId?: unknown;
  latitude?: number | string;
  location?: unknown; // Geo-fence landmark
  longitude?: number | string;
  messageUuid?: string;
  name?: string;
  OBJECTID?: number;
  payload?: GenericObject;
  postalCode?: string;
  rssi?: RssiType | number;
  state?: string;
  street?: string;
  shockEvent?: string;
  tiltEvent?: string;
  temperatureLevelValue?: string;
  temperatureLevelUnits?: string;
  lightLevelValue?: string;
  lightLevelUnits?: string;
  inputChanged?: GenericObject;
  tagReports?: GenericObject;
  tripGroup?: string;
  shockBreach?: boolean;
  tiltBreach?: boolean;
  temperatureBreach?: boolean;
  lightBreach?: boolean;
  maxTemperatureThreshold?: string | number;
  minTemperatureThreshold?: string | number;
  maxLightThreshold?: string | number;
  minLightThreshold?: string | number;
  VideoURL?: string;
  isSevereEvent?: boolean;
};

// defines the ctc asset entity interface ...
export interface Asset extends CTCEntity {
  assetId?: number,
  assets?: Link[];
  name?: string,
  description?: string,
  externalId?: string,
  engineHoursReferenceTime?: Date,
  odometerReferenceTime?: Date,
  extensions?: {
    fuelTankCapacity?: number,
    fuelBurnRate?: number,
    overrideOdometer?: boolean,
    overrideEngineHours?: boolean,
    overrideEngineRunTime?: boolean,
    overrideSpeed?: boolean,
    scheduleName?: MaintenanceSchedule['name'] | MaintenanceSchedule
  },
  assetType?: AssetType,
  assetCategory?: string,
  installDate?: string,
  uninstallDate?: string,
  fuelType?: string,
  ioProfile?: Link,
  imageUrl?: string,
  licenseNumber?: string,
  state?: string,
  year?: string,
  make?: string,
  model?: string,
  vin?: string,
  schedules?: Link[];
  maintenanceSchedules?: Link[];
  tagReport?: TagReportType,
  tagReports?: TagReportsType,
  deviceEsn?: string,
  deviceId?: number,
  devices?: Link[],
  device?: {
    id?: string;
    title?: string;
    esn?: string;
    idType?: string;
    features?: object;
  },
  taskStatus?: any,
  ignition?: string | boolean,
  vbFuelPercent?: number,
  vbOdometer?: number,
  operators?: string;
  route?: string;
  schedule?: Link,
  maintenanceSchedule?: MaintenanceSchedule,
  adcDeviceBatteryVoltage?: number,
  adcExternalPowerVoltage?: number
  updateAccount?: boolean;
  hasExistingSchedule?: boolean;
  odometerReference?: number | string;
  engineHoursReference?: number | string;
  previousAccount?: Link;
  hadDevice?: boolean;
  removePreviousDevice?: boolean;
  lastModifiedOn?: Date | string;
  createdOn?: Date | string;
  odometer?: number | string;
  latitude?: number | string;
  longitude?: number | string;
  groups?: Link[];
};
export type Assets = Asset[];

// defines the arcgis cvs asset entity interface ...
export interface CVSAssetType extends Asset {
  adcDeviceBatteryVoltage?: number;
  adcExternalPowerVoltage?: number;
  additionalRiders?: string;
  altitude?: number;
  assets?: Link[];
  asset?: object;
  lastName?: string;
  firstName?: string;
  dataPumpPullTime?: number;
  deviceMessageSequenceNumber?: number;
  distance?: unknown;
  engineHours?: number;
  engineHour?: number;
  evBatteryPackRemainingCharge?: number;
  evCurrent?: number;
  evDerivedChargingState?: string;
  evHybridCurrent?: number;
  evHybridVoltage?: number;
  evRangeRemaining?: number;
  evVoltage?: number;
  eventCode?: number;
  eventType?: string;
  gpsFixStatusDiffCorrected?: string; // Stringified Boolean!
  gpsFixStatusHistoric?: string; // Stringified Boolean!
  gpsFixStatusInvalidFix?: string; // Stringified Boolean!
  gpsFixStatusInvalidTime?: string; // Stringified Boolean!
  gpsFixStatusLastKnown?: string; // Stringified Boolean!
  gpsFixStatusStatusPredicted?: string; // Stringified Boolean!
  gpsFixStatusTwoDFix?: string; // Stringified Boolean!
  gpsOdometer?: number; // could be negative number!
  gpsSpeed?: number;
  gpsValidity?: string; // Stringified Boolean!
  gridVisibility?: boolean;
  hdop?: number;
  heading?: number;
  idleHours?: number;
  ignition?: string | boolean;
  input1?: string;
  input2?: string;
  input3?: string;
  input4?: string;
  input5?: string;
  input6?: string;
  input7?: string;
  inputBitmap?: string;
  item?: Asset;
  jbusAmberWarningLamp?: unknown;
  jbusProtectLamp?: unknown;
  jbusRedStopLamp?: unknown;
  mapVisibility?: boolean;
  messageUuid?: string;
  OBJECTID?: number;
  operatorIds?: unknown;
  operatorNames?: string | null;
  postedSpeedLimit?: number;
  postedSpeedLimitUnits?: string;
  readonly type?: string,
  readonly properties?: {
    point_count: number,
  }
  reportType?: any;
  routeId?: unknown;
  routeName?: string;
  satellites?: number;
  speed?: number;
  systemOfUnits?: string;
  utilizationState?: unknown;
  vbEngineHours?: number;
  vbFuelPercent?: number;
  vbOdometer?: number;
  vbSpeed?: number;
  vbusMilStatus?: string; // Stringified Boolean!
  toolItems?: Array<object>;
  isExpand?: boolean;
  hoverFromMap?: boolean;
  isHover?: boolean;
  isMapClick?: boolean;
  utilization?: string;
};
export type CVSAssetTypes = CVSAssetType[];

export interface HVSRecord extends CVSAssetType {
  iOnVisionEvent?: VideoEvent | null;
  duration?: {
    hours: number;
    minutes: number;
  }; // added property for calculations
  tripGroup?: number;
  groupRowData?: boolean;
  crashDate?: Date;
  severityCode?: string;
  geofenceType?: string;
  inputChanged?: GenericObject;
  eventTime?: number;
  street?: string;
  city ?: string;
  state ?: string;
  postalCode?: string;
  item?: GenericObject;
  data?: GenericObject;
  isSevereEvent?: boolean;
  isCameraOnly?: boolean;
  deltaDistance?: number | null;
};
export type HVSRecords = HVSRecord[];
export interface CVSAssetAggregation extends CVSAssetType {
  method : {
    features?: CVSAssetType[],
    method?: string,
    minZoomLevel?: number,
    maxZoomLevel?: number,
    timestamp?: number
  }
};
export type CVSAssetAggregations = CVSAssetAggregation[];

export enum CrashboxxSeverityCode {
  VeryLight = 'VeryLight',
  Light = 'Light',
  Moderate = 'Moderate',
  Heavy = 'Heavy',
  VeryHeavy = 'VeryHeavy'
};

export enum CrashClassification {
  Unconfirmed = 'UNCONFIRMED',
  Confirmed = 'CONFIRMED',
  TechnicianInstall = 'TECHNICIAN_INSTALL',
  DeInstall = 'DE_INSTALL',
  ImproperInstallation = 'IMPROPER_INSTALLATION',
  LooseInstall = 'LOOSE_INSTALL',
  FalsePositive = 'FALSE_POSITIVE',
  Testing = 'TESTING'
};

export interface Crashboxx {
  caseId: number;
  accountId: number;
  accountIdType?: 'AccountType';
  altitude?: number;
  assetId: number;
  city: string;
  country: string;
  countryCd?: string;
  crashDate: Date;
  crashUuid?: string;
  createdOn?: Date;
  crush?: number;
  deviceId: number;
  deviceIdType: string;
  eventClassification: string;
  groups?: unknown;
  heading: number;
  id?: number;
  lastModifiedOn?: Date;
  latitude: number;
  licenseNumber?: string;
  longitude: number;
  make?: string;
  model?: string;
  ntfEngRequestId?: string;
  numberOfSatellites?: number;
  odometer?: number;
  pdof?: number
  rawData?: string;
  rssi?: number;
  sequenceNumber?: number;
  severityCode: CrashboxxSeverityCode;
  severityProcessedOn?: Date;
  speed: number;
  stateProvince: string;
  stateProvinceCd: string;
  street1: string;
  street2?: string
  vin?: string | number;
  vinRegion?: string;
  year?: string | number;
  zip: string | number;
};

export interface CrashboxxRecord {
  icn : Crashboxx;
}

export interface ToolAssetType extends Asset {
  asset: Link;
  assetCategory: null | string;
  assets: Link[];
  architecture?: string;
  color: unknown;
  createdOn: string;
  d2aOffset: unknown;
  dateSeedTaken: unknown;
  engineHoursOffset: unknown;
  engineHoursReferenceTime: Date;
  extendedAttribute: ExtendedAttribute;
  fuelEfficiencyCity: unknown;
  fuelEfficiencyCombined: unknown;
  fuelEfficiencyHighway: unknown;
  fuelTankCapacity: unknown;
  fuelType: unknown;
  groups: unknown;
  idLegalPerHour: unknown;
  ioprofile: Link;
  lastModifiedOn: string;
  licenseState: unknown;
  manufacturer: unknown;
  mappingIconId: unknown;
  odometerOffset: unknown;
  odometerReferenceTime: unknown;
  odometerReferenceUnit: string;
  operators: string;
  route: string;
  seedValue: unknown;
  address: {
    address1: string;
    addressLatitude: number;
    addressLongitude: number;
    city: string;
    country: string;
  }
  age: number;
  batchId: string;
  carrier: number
  commState: {
    available: boolean;
    connected: boolean;
    dataService: boolean;
    networkService: boolean;
    roaming: boolean;
  }
  device: Link;
  deviceAirId: string;
  deviceId: number;
  deviceMessageSequenceNumber: number;
  deviceName: string;
  eventTime: string;
  fixStatus: boolean;
  gpsFixStatus: {
    predicted: boolean;
    differentiallyCorrected: boolean;
    lastKnown: boolean;
    invalidFix: boolean;
    twoDFix: boolean;
  }
  hdop: number;
  heading: number;
  inAccountHierarchy: boolean
  latitude: number;
  longitude: number;
  macAddress: string;
  messageUuid: string;
  reportingDevice: Link;
  rssi: number;
  satellites: number;
  serialNumber: string;
  timeOfFix: string;
  verbose: boolean;
  uninstallDate: unknown;
  unpowered: boolean;
  useTelematics: unknown;
  vehicleCategoryId: unknown;
  vehicleTypeId: unknown;
  verified: boolean;
  vinRegion: string;
};
export type ToolAssetTypes = ToolAssetType[];

export interface TagReportType {
  account: Link;
  address: {
    address1: string;
    addressLatitude: number;
    addressLongitude: number;
    city: string;
    country: string;
    county: string;
    crossStreet: string;
    postalCode: string;
    postedSpeedLimit: number;
    roadTypeCode: string;
    speedLimitUnits: string;
    state: string;
    street?: string;
    stateProvinceCd: string;
    tollRoadFlag: string | boolean;
    timeZoneOffset: string;
    timeZoneId: string;
  },
  asset: Link;
  assetType: AssetType;
  batchId: string;
  commState: {
    carrier: number;
    available: boolean;
    connected: boolean;
    dataService: boolean;
    networkService: boolean;
    roaming: boolean;
    threeGNetwork: boolean;
    voiceCallActive: boolean;
  },
  device: Link;
  deviceAirId: string;
  deviceId: number;
  deviceMessageSequenceNumber: number
  deviceName: string;
  eventTime: string;
  fixStatus: boolean;
  gpsFixStatus: {
    predicted: boolean;
    differentiallyCorrected: boolean;
    lastKnown: boolean;
    invalidFix: boolean;
    twoDFix: boolean;
    historic: boolean;
    invalidTime: boolean;
  },
  hdop: number;
  heading: number;
  latitude: number;
  longitude: number;
  messageUuid: string;
  satellites: number
  timeOfFix: Date;
  mobileIdTypeString: string;
  reportingDevice: Link;
  verbose: boolean;
  macAddress: string;
  serialNumber: string;
  rssi: number
  age: number
  inAccountHierarchy: boolean;
};
export type TagReportTypes = TagReportType[];

export enum DeviceTypeCategory {
  LMU_TTU = 'LMU_TTU',
  ROUTER_VANGUARD = 'ROUTER_VANGUARD',
  ROUTER_FUSION = 'ROUTER_FUSION',
  BLE = 'BLE',
  OEM = 'OEM',
  AEMP = 'AEMP',
  ATU = 'ATU',
  ASSIST = 'ASSIST',
  DEFAULT_DEVICE_TYPE = 'DEFAULT_DEVICE_TYPE',
  HMU = 'HMU',
  LMU = 'LMU',
  PTAG = 'PTAG',
  SCI = 'SCI',
  STAG = 'STAG',
  TTU = 'TTU',
  TAG = 'TAG',
  VLU = 'VLU',
  VAN = 'VAN'
}

export enum DeviceTypeArchitecture {
  BIT_8 = 'BIT_8',
  BIT_16 = 'BIT_16',
  BIT_32 = 'BIT_32',
  SCI = 'SCI',
  VLU6R = 'VLU6R',
  Queclink = 'Queclink',
  BMW = 'BMW',
  Redtail = 'Redtail'
}

export interface DeviceType extends CTCEntity {
  name: string;
  category: DeviceTypeCategory;
  lastModifiedOn?: DateType;
  createdOn?: DateType;
  appId?: string;
  architecture?: DeviceTypeArchitecture;
};
export type DeviceTypes = DeviceType[];

export enum CarrierDeviceIdentifierType {
  ESN = 'ESN',
  MEID = 'MEID',
  ICCID = 'ICCID',
  MDN = 'MDN'
};

export enum DeviceIdentifierType {
  SERIAL_NUMBER = 'SERIAL_NUMBER',
  ESN = 'ESN',
  MEID = 'MEID',
  ICCID = 'ICCID',
  IMEI = 'IMEI',
  IMSI = 'IMSI',
  MDN = 'MDN'
};

export enum DeviceResultIdentifierType {
  SERIAL_NUMBER = 'serialNumber',
  ESN = 'esn',
  MEID = 'meid',
  ICCID = 'iccid',
  IMEI = 'imei',
  IMSI = 'imsi',
  MDN = 'mdn',
  EXTERNAL_ID = 'externalId',
  DEVICE_ID = 'deviceId',
  IP_ADDRESS = 'ipAddress',
  ID = 'id',
}

// defines the ctc Device Configuration entity interface ...
export interface DeviceConfiguration {
  id?: number;
  name: string;
  account?: Link;
  deviceType: Link;
  pegBehavior: Link;
  carrierCode: string;
  carrierDeviceIdentifierType: string;
  lastModifiedOn?: DateType;
  createdOn?: DateType;
  inheritable?: boolean;
};
export type DeviceConfigurations = DeviceConfiguration[];

// defines the dm Device File entity interface ...
export interface DeviceFile {
  account?: Link;
  createdOn?: DateType;
  deletedOn?: string;
  description?: string;
  fileVersion?: number;
  groups?: Links[];
  id?: number;
  inheritable?: boolean;
  inheritableAccounts?: number[];
  lastModifiedOn?: string;
  location?: string;
  name: string;
  _self: Link;
  status?: string;
  type?: string;
  validationMetaData?: ValidationMetaData;
  validationStatus?: ValidationStatus;
  version?: number;
};
export type DeviceFiles = DeviceFile[];

// Device File Validation Status Type
interface ValidationStatus {
  status?: string;
}

// Device File Validation Metadata Type
interface ValidationMetaData {
  deviceTypeId?: string | number;
  deviceTypeName?: string;
  fields?: {
    appId?: string | Fields;
    version?: string;
    fromVersion?: string;
    hostedApp?: Fields,
    vBus?: Fields,
    keyAlias?: Fields,
    reportedName?: Fields,
    updateAssignedDevices?: boolean;
  },
  fileTypeId?: string | number;
  fileTypeName?: string;
  platformId?: string;
  platformName?: string;
  releaseNotesName?: string;
  releaseNoteAttached?: boolean;
  releaseNotesUrl?: string;
  autoJobId?: string;
  autoJobDevicesAffected?: number;
}

export interface Job extends CTCEntity {
  description?: string;
  criteria?: GenericObject[];
  tasks?: DownloadJobTasks;
  progressStatus?: DownloadJobType;
}

export interface ErrorText {
  error? : {
    code?: number;
    description?: string;
  }
};

export interface CreateJob extends Job {
  deviceTypes?: Array<PlatformDeviceType>;
  fileTypes?: Array<string>;
  fileLists?: Array<string>;
  jobId?: number;
  jobName?: string;
  description?: string;
  version?: number;
  selectedDevicesList?: Array<string>;
  fileNameIsMissing?: boolean;
  isDuplicateFileExist?: boolean;
  jobNameisEmpty?: boolean;
  searchCriteria?: Array<string>;
  isNoDevicesAvailable?: boolean;
  isLastDevice?: boolean;
  noTaskCreated?: boolean;
  isAllFilesLoaded?: boolean;
}

// defines the dm Download Job entity interface ...
export interface DownloadJob extends Job {
  createdOn?: DateType;
  name?: string;
  _self?: Link;
  account?: Link;
};

export type DownloadJobs = DownloadJob[];

//defines the download jobs types enumeration ...
export enum DownloadJobType {
  Pending             = 'Pending',
  Created             = 'Created',
  Failed              = 'Failed',
  Completed           = 'Completed',
  Inprogress          = 'InProgress',
  InprogressCancelled = 'InProgressCancelled',
  Cancelling          = 'Cancelling',
  Cancelled           = 'Cancelled',
  Validating          = 'Validating',
  Validated           = 'Validated'
};

export type DownloadJobTypes = DownloadJob[];

// defines the dm Download Job task entity interface ...
export interface DownloadJobTask {
  deviceType?: DeviceTypeCategory;
  fileType?: string;
  counts?: GenericObject;
  createdOn?: string;
  criteria?: GenericObject[];
  deletedOn?: string;
  description?: string;
  index?: number;
  job?: unknown;
  lastModifiedOn?: string;
  metaData?: GenericObject;
  name?: string;
  progressStatus?: string;
  status?: string;
  taskType?: string;
  usesAppIds?: boolean;
  appId?: string;
  deviceAppIds?: string[];
  platformId?: string | number;
  version?: number;
  _self?: Link;
};

export interface DownloadJobTasksElem {
  task: DownloadJobTask
};

export type DownloadJobTasks = DownloadJobTasksElem[];

// Generic Service Field Type
export interface Field {
  description?: string;
  id?: number;
  value?: string;
}

export type Fields = Field[];

// Device File Maintain (Add / Edit) request.
export interface MaintainDeviceFileRequest {
  account?: Link;
  createdOn?: DateType;
  deletedOn?: string;
  description?: string;
  groups?: Links[];
  id?: number;
  inheritable?: boolean;
  inheritableAccounts?: number[];
  lastModifiedOn?: string;
  location?: string;
  name: string;
  _self?: Link;
  status?: string;
  releaseNotesUrl?: string;
  validationMetaData?: ValidationMetaData;
  validationStatus?: ValidationStatus;
  type?: string;
  version?: number;
  releaseNoteAttached?: boolean;
}

// defines the dm Device File entity interface ...
export interface PlatformDeviceType {
  id: string;
  name: string;
  platformName?: string;
  fileTypes: Array<PlatformFileType>;
};
export type PlatformDeviceTypes = PlatformDeviceType[];

// defines the dm Device File entity interface ...
export interface LookupDataOption {
  description: string;
  id: number;
  value: string;
};
export type LookupDataOptions = LookupDataOption[];

// Defines the input field type of a Platform File.
export interface InputField {
  label?: string;
  name?: string;
  required?: boolean;
  type?: string;
  showOnEdit?: boolean;
}

// defines the dm Device File entity interface ...
export interface PlatformFileType extends CTCEntity {
  deviceTypeId: string;
  downloadable: boolean;
  fileEntityType: string;
  fileInheritable: GenericObject;
  inputFields: Array<InputField>;
  name: string;
  platformId: string;
  platformName: string;
};
export type PlatformFileTypes = PlatformFileType[];

// defines the ctc actionTypes entity interface ...
export interface ActionTypes {
  smsTemplate: GenericObject;
  urlTemplate: GenericObject;
  mobileTemplate: GenericObject;
  emailTemplate: GenericObject;
};
// defines the ctc Notification Type entity interface ...
export interface NotificationType extends CTCEntity{
  name: string;
  actionTemplates?: Link[];
  inheritable?: boolean;
  systemDefined?: string;
  lastModifiedOn?: string;
  rawLastModifiedOn?: string;
  status?: EntityStatus;
  description?: string;
  triggerConjunction?: string;
  triggerNotification?: GenericObject;
  triggers?:GenericObject;
  actionTemplatesList?: GenericObject;
  triggerAttributes?: GenericObject;
  actionNotification?: ActionTypes;
  actionNotifications?: GenericObject;
  actionType?: string;
  createdOn?: string;
  deletedOn?: string;
  groups?: Link[];
  controlsObj?: GenericObject;
};

export type NotificationTypes = NotificationType[];

export enum TemplateType {
  smsTemplate = 'smsTemplate',
  urlTemplate = 'urlTemplate',
  emailTemplate = 'emailTemplate',
  mobileTemplate = 'mobileTemplate',
};

// Constant for "iOn Geofence Event v2" Notification Type
export const ION_GEOFENCE_EVENT_V2 = 'iOn Geofence Event v2';

export type GeoFenceAttributes = {
  OBJECTID?: number | string | undefined;
  landmarkAccount?: string;
  landmarkAccountId?: number;
  landmarkName?: string;
  landmarkStatus?: EntityStatus;
  geofenceType?: string;
  landmarkCategory?: string;
  landmarkRestricted?: string;
  createdDate?: number;
  modifiedDate?: number;
  geometry?: any;
  isEditGeofenceRestricted?: boolean;
};

export type Geofences = GeoFenceAttributes[];

// defines the props definitions for geofence status
export type GeoFenceStatus = {
  success: boolean;
  isCreate: boolean;
  isDelete: boolean;
  isUpdate: boolean;
};

// GeoFence Actions enum
export enum GeoFenceActions {
  Create = 'create',
  Delete = 'delete',
  Update = 'update'
};

/**
 * Defines the CTC Alert Configuration Types. This type is used interchangable with
 * the Notification Type alias.
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveUsersAlertConfigurations
 */
export interface AlertConfiguration extends CTCEntity {
  name?: string;
  description?: string;
  groups?: Link[];
  alertActionLinks?: Link[];
  triggerValues?: string;
  placeHolders?: string;
  createdOn?: string;
  activatedOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  snoozeDuration?: number;
  alertType?: Link;
  schedule?: Link;
  scheduleEvaluation?: string;
  severity?: AlertSeverity;
  processForSubAccounts?: string;
  expanded?: GenericObject;
  extendedAttribute?: ExtendedAttribute;
  actionsData?: GenericObject;
  'account.name'?: string;
  alertConfigurationId?: number;
};
export type AlertConfigurations = AlertConfiguration[];

// AlertConfiguration convenience alias for application specific semantics ...
export interface Notification extends AlertConfiguration { };
export type Notifications = Notification[];

/**
 * Defines the valid CTC AlertSeverity Types
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveAllAlertActions
 */
export enum AlertSeverity {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  SYSTEM = 'SYSTEM'
};
export type AlertSeverities = AlertSeverity[];

/**
 * Defines the valid CTC Alert Action Types
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveAllAlertActions
 */
export enum AlertActionTypes {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  URLPOST = 'URLPOST',
  MOBILE_PUSH = 'MOBILE_PUSH'
};

/**
 * Defines the valid CTC Alert Action Schedule Ref
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveAllAlertActions
 */
export enum AlertActionScheduleRef {
  INSIDE = 'IN',
  OUTSIDE = 'OUT'
};

/**
 * Defines the valid CTC AlertType Trigger Conjunction definitions
 *
 * @see https://connect.calamp.com/connect/#!/alerts/getTriggerConjunctionValues
 */
export enum AlertTypeTriggerConjunction {
  AND = 'AND',
  OR = 'OR'
};

/**
 * Defines the valid CTC AlertActionType definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/getAlertActionTypeValues
 */
export interface AlertActionType extends CTCEntity {
  groups?: Link[]
  expanded?: GenericObject;
  alertActionTypes?: AlertActionTypes[];
};

/**
 * Defines the valid CTC AlertType definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveUsersAlertTypes
 */
export interface AlertType extends CTCEntity {
  groups?: Link[]
  triggerConjunction?: AlertTypeTriggerConjunction;
  name?: string;
  description?: string;
  activatedOn?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  account?: Link;
  triggers?: Link[];
  actionTemplates?: Link[];
  inheritable?: boolean;
  systemDefined?: boolean;
};

export type AlertActionTemplates = {
  alert?: AlertConfiguration,
  action?: AlertAction,
  templates?: AlertActionTemplate[]
};

// defines the alert action destination definitions ...
export type DestinationType = {
  email?: {
    to: Array<string>;
    subject: string;
    cc?: Array<string> | [];
  };
  sms?: {
    to: Array<string>;
  };
  http?: {
    username: string;
    password: string;
    url: string;
  };
  mobilePush?: {
    appName: string;
    audience: any;
  };
}

/**
 * Defines the valid CTC AlertAction Type definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieve_0
 */
export interface AlertAction extends CTCEntity {
  groups?: Link[];
  name?: string;
  destinations?: DestinationType;
  description?: string;
  activatedOn?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  actionType?: AlertActionTypes;
  expanded?: GenericObject;
  alertActionTemplate?: Link;
  alertConfiguration?: Link;
  extendedAttribute?: ExtendedAttribute
};

/**
 * Defines the valid CTC Alert Action Template Type definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/getAlertActionTemplateForAlertType
 */
export interface AlertActionTemplate extends CTCEntity {
  groups?: Link[];
  description?: string;
  activatedOn?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  title?: string;
  body?: string;
  from?: string;
  contentType?: string;
  xsltTransform?: string;
  localeld?: string;
  actionType?: AlertActionType
  expanded?: GenericObject;
  inheritable?: boolean;
  systemDefined?: boolean;
};

/**
 * Defines the valid CTC AlertTrigger Type definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveAllAlertTriggers
 */
export interface AlertTrigger extends CTCEntity {
  groups?: Link[]
  description?: string;
  triggerExpression?: string;
  evaluatedTriggerExpression?: string;
  placeHolders?: GenericObject;
  activatedOn?: string
  createdOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  expanded?: GenericObject;
  alertType?: Link;
};

/**
 * Defines the valid CTC AlertFired Type definition
 *
 * @see https://connect.calamp.com/connect/#!/alerts/retrieveAllAlertActions
 */
export interface AlertFired extends CTCEntity {
  createdOn?: string;
  acknowledgedOn?: string;
  avlDateTime?: number | string;
  acknowledgedBy?: string;
  alertConfiguration?: Link;
  device?: Link;
  asset?: Link;
  alertType?: Link;
  alertTriggers?: Link[];
  esn?: string;
  acknowledgedUserName?: string;
  deviceIdentifierType?: string;
  metaAlertEventType?: string;
  geoEventId?: string | number;
  geoEventName?: string;
  geoEventType?: string;
  geoEventduration?: string | number;
  geoEventLandMarkId?: string | number;
  geoEventLandMarkName?: string;
  geoEventLandMarkCategory?: string;
  avlMessageUuid?: string;
  avlEventType?: string;
  imei?: null;
  meid?: null;
  imsi?: null;
  iccid?: null;
  deviceType?: null;
  assetName?: string;
  assetType?: string;
  assetCategory?: string;
  vin?: string | number;
  vinRegion?: string;
  fuelType?: string;
  make?: string;
  model?: string;
  licenseState?: string;
  licenseNumber?: string;
  unpowered?: string;
  accountName?: string;
  solomonId?: string | number;
  accountTimeZone?: string;
  unitOfMeasure?: string;
  subscriptions?: unknown;
  severity?: AlertSeverity;
  alertConfigurationName?: string;
  alertTypeName?: string;
  assetId?: number;
  deviceIdentifier?: number;
  acknowledgedById?: string | number;
  avlEventCode?: number;
  year?: string;
  longitude?: string | number;
  latitude?: string | number;
  isAcknowledged?: boolean;
  alertConfigurationId?: number;
  address?: {
    city?: string;
    country?: string;
    county?: string;
    crossStreet?: string;
    postalCode?: number;
    postedSpeedLimit?: number;
    speedLimitUnits?: string;
    state?: string;
    street?: string
  };
  uuid?: string;
  alertActions?: GenericObject[];
  assetCurrentOdometer?: number;
  assetCurrentEngineHours?: number;
  maintenanceAssetServiceItemId?: number;
  maintenanceAssetServiceItemName?: string;
  maintenanceAssetNextServiceDaysOverdue?: number;
  maintenanceAssetNextServiceOdometer?: number;
  maintenanceAssetNextServiceEngineHours?: number;
  maintenanceAssetnNextServiceOdometerOverdue?: number;
  maintenanceAssetNextServiceEngineHoursOverdue?: number;
  maintenanceAssetNextServiceReminderFlag?: string | boolean;
  maintenanceAssetNextServiceDueFlag?: string | boolean;
  maintenanceAssetNextServiceDate?: string
};

// Device extensions type definition
export interface DeviceExtensions {
  scriptVersion?: string;
  configVersion?: string;
  firmwareVersion?: string;
  idReportTimestamp?: string;
  appId?: any;
  esnAssociatedLMU?: string;
  androidOSVersion?: string;
  kernelVersion?: string;
  isOdbBoardError?: string;
  vbusEnabledStatus?: boolean;
  vbusMode?: string;
  fileVersions?: any;
  associatedDevices?: GenericObject;
}

export interface HeartBeat {
  createdOn: string;
  deviceId: number;
  deviceIdType: string;
  firstHeartBeatDateTime: string;
  lastHeartBeatDateTime: string;
  lastModifiedOn: string;
};

export interface DeviceHeartBeat {
  accountId: number;
  accountIdType: string;
  deviceIdType: string;
  deviceIdList: Array<string>;
}

export interface TaskStatus {
  errorCodes?: any[] | [];
  status?: AsyncTaskStatus;
  updateTime?: string;
}

export interface DeviceIdentifiers {
  min?: number;
  esn?: string;
  iccid?: string;
  airId?: string;
  imsi?: string;
  meid?: string;
  imei?: string;
  mdn?: string;
  isdn?: string;
}

// Devices type definition ...
export interface Device extends CTCEntity, DeviceIdentifiers {
  icnEnabled?: boolean;
  name?: string;
  deviceName?: string;
  deviceNote?: string;
  carrierStatus?: string;
  externalId?: string;
  extensions?: DeviceExtensions;
  ipAddress?: string;
  shipDate?: string;
  networkMode?: string;
  activationDate?: string;
  deviceConfiguration?: Link;
  pegBehavior?: PegBehaviorType;
  groups?: any;
  provisionStatus?: string;
  serialNumber?: string;
  type?: Link;
  asset?: Link;
  heartBeatInfo?: HeartBeat;
  features?: {
    [key: string]: string;
  };
  category?: string | string[];
  architecture?: string;
  lastAvlCommunication?: LastAvlCommunicationType;
  typeName?: string;
  networkCarrier?: string;
  dmDevice?: boolean
  networkCurrentMode?: string;
  phoneNumber?: string;
  signalStrength?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  idReport?: DeviceIdReport;
  firmwareVersion?: any;
  gpsActivated?: boolean;
  iOnTagEnabled?: boolean;
  temperatureLogging?: boolean;
  sciDeviceSettings?: any;
  tagReports?: TagReportsType;
  lastReportedTime?: string;
  fileHistoryPending?: any;
  userDefinedMobileId?: any;
};
export type Devices = Device[];

export interface DeviceIdReport extends DeviceIdentifiers {
  device?: {
    idReport: IdReport;
  };
  platform?: GenericObject;
  firstIdReportTime?: Date | string;
  isOdbBoardError?: boolean;
  isUnitStatus1?: boolean;
  isGPSTrackingOK?: boolean;
  isGPSAntennaStatusOK?: boolean;
  isGPSReceiverTestOK?: boolean;
}

export interface UploadUrlParams {
  accountId: number;
  entityType: string;
  actionType: string;
  userId: number;
}

export type IdReport = {
  otaCapability?: GenericObject;
  fileTable?: GenericObject;
  files?: GenericObject;
  deviceServices?: GenericObject;
  extensions?: GenericObject;
  standardExtensions?: GenericObject;
  radioStateExtension?: GenericObject;
  otaStatus?: GenericObject;
  linuxDeviceFiles?: GenericObject;
  failsafeStatus?: GenericObject;
  deviceHealthStatus?: GenericObject;
  ubootRecovery?: GenericObject;
  edgeApp?: GenericObject;
  buildNumber?: string;
  vehicleClass?: string;
}

// Type for a device total count response
export interface DeviceTotalCountType {
  totalCount: number;
};

// Type for aggregation buckets from the device search response
export interface DeviceAggregationsBucketContentsType {
  key: string,
  doc_count: number
};

// Response type of the device search endpoints
export interface DeviceAggregationsResultType extends DeviceTotalCountType {
  aggregations: {
    deviceActivity: {
      buckets: Array<DeviceAggregationsBucketContentsType>;
    },
    deviceModel: {
      buckets: Array<DeviceAggregationsBucketContentsType>;
    }
  }
};

// Response type of the device firmware version search call
export interface DeviceAggregationsFirmwareVersionsResponseType extends DeviceTotalCountType {
  aggregations: {
    firmwareVersion: {
      buckets: Array<DeviceAggregationsBucketContentsType>;
    }
  }
};

// Response type of the device configuration version search call
export interface DeviceAggregationsConfigurationVersionsResponseType extends DeviceTotalCountType {
  aggregations: {
    configVersion: {
      buckets: Array<DeviceAggregationsBucketContentsType>;
    }
  }
};

// Response type of the device configuration version search call
export interface DeviceAggregationsAppIdResponseType extends DeviceTotalCountType {
  aggregations: {
    appId: {
      buckets: Array<DeviceAggregationsBucketContentsType>;
    }
  }
};

// Job Status type definition ...
export interface JobStatus {
  name?: string;
  createdAt?: string;
  username?: string;
  status?: string;
  id?: string;
  other?: any;
  deviceFeatures?: any;
  esn?: string;
  lastModifiedAt?: string;
  lastModifiedOn?: string;
};

export enum APIEntities {
  DEVICES = 'devices',
  ASSETS = 'assets',
  ROLES = 'roles',
  PROVISIONED_DEVICES = 'provisionedDevices',
  FILE_METAS = 'filemetas'
}

export interface DeviceAPIResult extends Device {
  deviceId?: number;
}

export interface AssetAPIResult extends Asset {
  assetId?: number;
}

export interface RoleAPIResult extends Role {
  roleId?: number;
}

export interface ExpandedInfo {
  response: {
    body: string;
    statusCode: number;
  }
}

export interface SubtaskError {
  error: {
    code: string;
    description: string;
  }
};

export interface SubtaskErrors {
  code: string;
  description: string;
};

export interface BulkGroupAssignmentInfo {
  isBulkOperation: boolean;
  setRowGroup: (rowGroup: boolean) => void;
  errors?: SubtaskError[];
  error?: SubtaskErrors;
};

export interface Subtask {
  deviceFeatures?: any;
  createdAt: number;
  id: string;
  lastModifiedAt: number;
  mainTaskId: string;
  name: string;
  info : ExpandedInfo;
  other: {
    entityType: BulkActionEntity;
    requestType: BulkActionEntity;
    bulkOperation: BulkOperation;
    taskNickName: string;
    features: any;
    version?: number;
  }
  status: AsyncTaskStatus;
  [APIEntities.DEVICES]: DeviceAPIResult[];
  [APIEntities.ASSETS]: Asset[];
  [APIEntities.ROLES]: Role[];
  [APIEntities.PROVISIONED_DEVICES]: InventoryDevice[];
  [APIEntities.FILE_METAS]: DeviceFile[]
}

// Accounts type definition ...
export interface Account extends CTCEntity {
  name?: string;
  activatedOn?: string;
  addresses?: string[];
  ancestors?: Link;
  applications?: Link[];
  autoAssetAssociationEnabled?: boolean;
  autoAssetCreationEnabled?: boolean;
  canViewAirtimeStore?: boolean;
  canViewHardwareStore?: boolean;
  canViewSubAccounts?: boolean;
  checkPulsDeviceOwnership?: boolean;
  createAssetOnFirstMessage?: boolean;
  createdOn?: string;
  creditTerms?: string;
  customerProfile?: unknown;
  deletedOn?: string;
  disabled?: boolean;
  enterprise?: boolean;
  extendedAttribute?: ExtendedAttribute;
  externalRefID?: number;
  groups?: string | string[] | Link[];
  hardwareDiscount?: number;
  href?: string;
  labels?: string;
  lastModifiedOn?: string;
  parent?: Link | number | string;
  phoneNumbers?: string[];
  pointOfContacts?: unknown;
  solomonId?: number | string;
  splitTagMessages?: boolean;
  subAccounts?: {
    subaccount?: Link
  }[] | string;
  subscription?: Link | number | string;
  timeZone?: string;
  language?: string;
  title?: string;
  unitOfMeasure?: string;
  accountContacts?: Contact;
  serviceProfile?: ServiceProfile;
  messageEndpoint?: MessageEndpoint;
  apiAccess: boolean;
  users: Users;
  ssoConfig?: Link | string;
  ssoMode?: string;
  inheritAllSubscriptionsFromParent?: boolean;
  doDeleteMsgEP?: boolean;
};
export type Accounts = Account[];

// SSO Configuration Apps
export interface ssoApps {
  app_id: number | string;
  client_id: string;
  client_secret?: string;
  redirect_uri?: string;
  scope?: string;
}

// SSO Configuration type definition
export interface SSOConfiguration {
  discovery_endpoint: string;
  domains: string[] | number[];
  account_ids: number[] | string[];
  sso_mode?: string;
  apps?: ssoApps[];
  id?: string;
}

// Type of an Account's Message Endpoint
export interface MessageEndpoint extends CTCEntity {
  configuration: MessageEndpointConfiguration;
}

// Type of an Account's Message Endpoint Configuration entity
export interface MessageEndpointConfiguration {
  awsAccessKeyId?: string;
  awsSecretAccessKey: string;
  queueName?: string;
  streamName?: string;
  region?: string;
  type: MessageEndpointTypes;
  bootstrapServers?: string;
  topicName?: string;
  username?: string;
  password?: string;
}

// Account Message Endpoint Types
export enum MessageEndpointTypes {
  AWS_KINESIS = 'AWS_KINESIS',
  AWS_SQS = 'AWS_SQS',
  KAFKA_MTLS = 'KAFKA_MTLS',
  KAFKA_SASL_PLAIN = 'KAFKA_SASL_PLAIN',
  KAFKA_SASL_SCRAM = 'KAFKA_SASL_SCRAM'
}

// Accoutn Message Endpoint Names
export enum MessageEndpointNames {
  STREAM_NAME = 'STREAM_NAME',
  QUEUE_NAME = 'QUEUE_NAME'
}

// Schedule Days enum ...
export enum ScheduleDays {
  Sunday = 'SUNDAY',
  Monday = 'MONDAY',
  Tuesday = 'TUESDAY',
  Wednesday = 'WEDNESDAY',
  Thursday = 'THURSDAY',
  Friday = 'FRIDAY',
  Saturday = 'SATURDAY'
};

// Schedule Configuration type definition ...
export type ScheduleConfiguration = {
  day?: ScheduleDays;
  time?: {
    startTime?: string;
    endTime?: string;
  }[];
};

// Schedules type definition ...
export interface Schedule extends CTCEntity {
  name?: string;
  createdOn?: Date | string;
  lastModifiedOn?: Date | string;
  activatedOn?: Date | string;
  tolerance?: number;
  timeZone?: string;
  inheritable?: boolean;
  description?: string;
  scheduleConfigs?: ScheduleConfiguration[];
};
export type Schedules = Schedule[];

// defines the ui specific augmented ctc schedule entity ...
export interface EnhancedSchedule extends Schedule {
};

export type MaintenanceSchedule = {
  name?: string;
  description?: string;
  scheduleId?: number;
  accountId?: number
  assetCategory?: string;
  assetType?: string
  createdOn?: Date | string;
  lastModifiedOn?: Date | string;
  status?: EntityStatus;
  version?: number;
  serviceItems?: MaintenanceServiceItems;
  group?: string;
  accountName?: string;
};
export type MaintenanceSchedules = MaintenanceSchedule[];

export type MaintenanceServiceItem = {
  serviceItemId?: number;
  name?: string;
  description?: string;
  intervalDays?: number;
  reminderDays?: number;
  intervalOdometer?: number;
  odometerUOM?: number;
  reminderOdometer?: number;
  intervalEngineHours?: number;
  reminderEngineHours?: number;
  accountId?: number;
  group?: string;
  createdOn?: string;
  lastModifiedOn?: string;
};
export type MaintenanceServiceItems = MaintenanceServiceItem[];

// IO Profile Port type definition ...
export type IOPort = {
  groups?: Link[],
  name?: string,
  index?: number,
  portType?: string,
  lowStatus?: string,
  highStatus?: string,
  pin?: number,
  workingIdle?: boolean,
};
export type IOPorts = IOPort[];

// IOProfiles type definition ...
export interface IOProfile extends CTCEntity {
  createdOn?: string;
  activatedOn?: string;
  lastModifiedOn?: string;
  deletedOn?: string;
  groups?: string;
  inheritable?: boolean;
  ports?: IOPort[];
  profileName?: string;
};
export type IOProfiles = IOProfile[];

// defines the ui specific augmented ctc ioprofile entity ...
export interface EnhancedIOProfile extends IOProfile {
}

// Operators & Drivers type definition ...
export interface Operator extends CTCEntity {
  firstName?: string;
  lastName?: string;
  activationDate?: string;
  asset?: Link;
  association?: string
  badgeNumber?: string;
  createdOn?: string;
  deletionDate?: string;
  extendedAttribute?: ExtendedAttribute;
  externalId?: string;
  groups?: string;
  lastModifiedOn?: string;
  operatorCategory?: string;
  phoneNumbers?: PhoneNumber[];
  schedules?: Link[];
  type?: string;
};
export type Operators = Operator[];

// defines the ui specific augmented ctc operator & driver entity ...
export interface EnhancedOperator extends Operator {
}

export interface Driver extends Operator {
};
export type Drivers = Driver[];
export interface EnhancedDriver extends Driver {
}

export interface Route extends CTCEntity {
  groups?: Link[];
  extendedAttribute?: ExtendedAttribute;
  status?: EntityStatus;
  description?: string;
  type?: string;
  category?: string;
  account?: Link;
  inheritable?: boolean;
  createdOn?: string;
  lastModifiedOn?: string;
  activationDate?: string;
  deletionDate?: string;
  polyline?: string;
  lineBuffer?: number;
  name?: string;
}
export type Routes = Route[];

// defines the inherited group properties for entity types ...
export enum GroupTypeEntity {
  asset = 'asset',
  operator = 'operator',
  route = 'route',
  geofence = 'geofence'
};

// defines the inherited group properties for entity categories ...
export enum GroupCategoryEntity {
  asset = 'asset',
  operator = 'operator',
  route = 'route',
  landmark = 'landmark',
  geofence = 'geofence'
};

// defines the micro-service inheritable properties value types ...
export type InheritablePropertyValue = (
  string | number | boolean | string[] | number[] | boolean[]
);

// defines the micro-service inheritable properties type ...
export type InheritableProperty = {
  account?: Link;
  value?: InheritablePropertyValue;
};

export enum ReportStatus {
  new = 'new',
  legacy = 'legacy'
}
export interface Report {
  accountId?: number;
  applicationName?: string[];
  bboxBuffer?: unknown;
  categoryName: string;
  chartData?: unknown;
  chartType?: string;
  childReports?: unknown[];
  clientReportView?: unknown;
  cloneParentId?: unknown;
  cloneParentName?: unknown;
  cloneable?: boolean;
  defaultIntervalTime?: unknown;
  description: string;
  externalSystemId?: number;
  id?: number;
  intervalTime?: unknown;
  intervalTimeOptions?: unknown;
  jasperReportDetailUrl?: string;
  jasperReportUrl?: string;
  legacy?: boolean;
  maxZoom?: unknown;
  minZoom?: unknown;
  name: string;
  parameters?: unknown;
  parentReportId?: unknown;
  reportExecutionType?: string;
  reportType?: string;
  reportStatus?: ReportStatus;
  subaccountVisible?: boolean;
  userLocale?: string[];
};

export interface DateFilterOptions {
  startDate?: Moment;
  endDate?: Moment;
  period?: string;
  selected?: Array<any>;
};

export interface SavedReport {
  creationDate: Date;
  description: string;
  label: string;
  permissionMask: number;
  resourceType: string;
  updateDate: Date;
  uri: string;
  version: number;
}

export interface ScheduledReport {
  description: string;
  id: number;
  reportId?: number;
  label: string;
  owner: string;
  reportLabel: string;
  reportUnitURI: string;
  state: {
    nextFireTime: Date;
    value: string;
  }
  version: number;
}

export type AuditChange = {
  fieldName: string,
  links: Link[] | [],
  newValue: string,
  oldValue: string
}
export type AuditChanges = AuditChange[];

export type AuditRecord = {
  action: string,
  applicationKey: string,
  auditId: string,
  changes: AuditChange[],
  createdOn: string,
  entity: string,
  entityId: string,
  entityType: string,
  links: Link[]
  user: string,
  userAccount: string,
}
export interface ServiceSchedule {
  accountId: number;
  assetId: number;
  category?: string;
  dashboardDue?: number;
  engineHours?: number;
  name: string;
  nextReminderDue: boolean;
  nextServiceProgress?: number;
  odometer?: number;
  scheduleId?: number
  scheduleName: string;
  status: EntityStatus;
  type: AssetType;
};

export interface ServiceItem {
  assetId: number;
  createdOn?: Date;
  dashboardDue?: number;
  dashboardDueRow?: boolean
  lastModifiedOn?: Date;
  lastServiceDate?: Date;
  lastServiceEngineHours?: number;
  lastServiceOdometer?: number;
  nextReminderDate?: Date;
  nextReminderDue?: boolean
  nextReminderEngineHours?: number;
  nextReminderOdometer?: number;
  nextServiceDate?: Date;
  nextServiceDateProgress?: number;
  nextServiceEngineHours?: number;
  nextServiceEngineHoursProgress?: number;
  nextServiceOdometer?: number;
  nextServiceOdometerProgress?: number;
  nextServiceProgress?: number;
  odometerUOM?: string;
  serviceItemId: number;
  serviceItemName: string;
  version?: number;
}

export interface ServiceLog {
  assetId: number;
  assetServiceLogId?: number;
  createdOn?: Date;
  engineHours?: number | string;
  engineHoursDue?: number;
  laborCost?: number;
  lastModifiedOn?: Date;
  loggedByUserId?: number;
  miscCost?: number;
  notes?: string;
  odometer?: number | string;
  odometerDue?: Date;
  odometerUOM?: string;
  partsCost?: number;
  scheduleId?: number;
  serviceDate?: Date;
  serviceDateDue?: Date;
  serviceItemId: number;
  serviceItemName: string;
  serviceProvider?: string;
  status?: EntityStatus
  tax?: number;
  totalCost?: number;
  version?: number;
};

// Bulk assets response
export interface BulkAssetsResponse {
  results: Assets;
  errors: any[];
};

export interface BulkDevicesResponse {
  results: Devices;
  errors: any[];
};

export interface BulkGroupsResponse {
  results: GroupMembers;
  errors: any[];
};

// defines the type definitions for validateAttributes params ...
export type ValidateAttributesType = {
  entities: Array<{[key: string]: CTCEntity}>;
  entityName: string;
  propName: string;
  props: Results | string[];
};

// defines Latitude/Longitude params for Geofence coordinates
export type LatLong = {
  latitude: number;
  longitude: number;
};

export interface LatLongGroup {
  lat1: LatLong['latitude'];
  long1: LatLong['longitude'];
  lat2: LatLong['latitude'];
  long2: LatLong['longitude'];
};

// defines selected Geofence params to search by
export interface FindNearbyGeofence extends LatLong {
};

// defines Find Nearby feature type to search by
export enum FindNearbyType {
  asset    = 'asset',
  geofence = 'geofence',
  location = 'location'
};

export type ReportEntity = {
  id?: number;
  accountId?: number;
  applicationName?: string[];
  cloneParentId?: string;
  cloneParentName?: string;
  cloneable?: boolean;
  description?: string;
  name?: string;
  reportName?: string;
  reportType?: string;
  subaccountVisible?: boolean;
  variables?: string[];
  selectedDate?: Array<Moment>;
  dateRange?: string;
  accountIdForQuery?: number;
  associatedEmails?: string[];
  emailMessage?: string;
  outputFormat?: string;
  reportId?: number;
  scheduleAccountId?: number;
  scheduleDayOfMonth?: string;
  scheduleDaysOfWeek?: Array<number>;
  scheduleEnd?: string;
  scheduleStart?: string;
  scheduleTime?: string;
  scheduleType?: string;
  timezone?: string;
  customNote?: string;
  emailSubject?: string;
  startDate?: string;
  endDate?: string;
  primaryRecipientsEmail?: string[];
  repearEveryDay?: string;
  reportEmailContent?: string;
  secondaryRecipientsEmail?: string[];
  startTime?: string;
  selectedWeeks?: Array<number>;
  resultSendType?: string;
  lastDateRun?: GenericObject;
  lastRunMessage?: GenericObject;
  nextFire?: GenericObject;
  sendEmail?: boolean;
  report?: any;
};

export type ReportEntitys = ReportEntity[];

export interface Contact extends CTCEntity {
  firstName?: string;
  lastName?: string;
  email?: string;
  title?: string;
  addresses?: any[];
  phoneNumbers?: any[];
  deletedOn?: null;
};
export type Contacts = Contact[];

// Defines the type for associated asset information
export type AssociatedAssetType = {
  originatingAssetId?: number,
  id?: number;
  type?: string;
  name?: string;
};

// sensor data we get from the API response
export interface SmarterTrailerSensorData {
  account: AccountLink;
  device: Link;
  mobileId: string;
  mobileIdType: string;
  receiverId: string;
  messageTime: Date;
  messageUuid: string;
  sensorType: SensorTypes;
  data: {
    sensorTag: string;
    sensorId: string;
    details: {
      label: string;
      value: string;
    }[];
  }[]
}

// sesnor data after we augment it preserving the original
// data as rawData and converting the more usable data to the data attribute
export interface AugmentedSmarterTrailerSensorProperty {
  [key: string] : {
    account: AccountLink;
    device: Link;
    mobileId: string;
    mobileIdType: string;
    receiverId: string;
    messageTime: Date;
    messageUuid: string;
    accelerometerX: string;
    accelerometerZ: string;
    highPressure?: boolean;
    lowPressure?: boolean;
    highTemperature?: boolean;
    highPressureAlarm?: boolean;
    lowPressureAlarm?: boolean;
    temperatureAlarm?: boolean;
    label?: string;
    batteryVoltage: {
      value: string;
      units: string;
    };
    frameId: string;
    doorHistory: DoorHistoryItem[];
    issues?: {
      issue: SmarterTrailerSensorProperties;
      delta: number;
    }[];
    pressure: {
      value: string;
      units: string;
    };
    sensorId: string;
    sensorTag: 'TD1' | 'TD2' | 'WD1' | 'H' | 'DS';
    temperature: {
      value: string;
      units: string;
    };
    vehicle: number;
    wheelDisplayName: TireAndWheelDisplayMapping;
    wheelLocation: VehicleWithTrailerTires;
  };
}

export interface AugmentedWeightSensorProperty {
  [key: string] : {
    value: string;
    units: string;
    messageTime?: string | Date;
  };
}

// sesnor data after we augment it preserving the original
// data as rawData and converting the more usable data to the data attribute
export interface AugmentedCargoSenseProperty {
  [key: string] : {
    account: AccountLink;
    device: Link;
    height: {
      label: string;
      value: string;
    }
    mobileId: string;
    mobileIdType: string;
    receiverId: string;
    messageTime: Date;
    messageUuid: string;
    sensorId?: string;
    sensorTag: 'TD1' | 'TD2' | 'WD1' | 'H' | 'DS';
    status: {
      label: string;
      value: string;
    }
  };
}

export interface AugmentedBrakeSensorProperty {
  messageTime: 'string';
  data : AugmentedBrakeData;
}

export interface BrakeTypeProps {
  value: string;
  status: string;
  units?: string;
  isEngaged?: boolean;
}

export interface AugmentedBrakeType {
  service?: BrakeTypeProps;
  parking?: BrakeTypeProps;
  wear?: string;
}

export interface AugmentedBrakeData {
  frontLeft?: AugmentedBrakeType;
  frontRight?: AugmentedBrakeType;
  rearLeft?: AugmentedBrakeType;
  rearRight?: AugmentedBrakeType;
  parkingBrakeStatus?: BrakeChamberStatusType;
  isDragAlert?: boolean;
}

// door history item properties
export interface DoorHistoryItem {
  batteryLevel: TrailerStatus;
  doorState: TrailerDoorStatus;
  messageTime: Date;
}

export interface SmarterTrailerIssues {
  lowPressure?: boolean
  highPressure?: boolean
  highTemperature?: boolean
  lowPressureAlarm?: boolean
  highPressureAlarm?: boolean
  temperatureAlarm?: boolean
}

export interface DeviceServices {
  accountId?: number;
  taskNickName?: string;
  serviceName?: string;
  serviceStatus?: string;
  devices?: APIGatewaySearch | APIGatewaySmartSearch;
};

// the augmented data property with easier to consume properties
export interface AugmentedSmarterTrailerSensorData {
  DoorState: AugmentedSmarterTrailerSensorProperty;
  CargoSense: AugmentedCargoSenseProperty;
  WETS: AugmentedSmarterTrailerSensorProperty;
  TPMS: AugmentedSmarterTrailerSensorProperty;
  BMS : AugmentedBrakeSensorProperty;
  WMS: AugmentedWeightSensorProperty;
  LMS: AugmentedSmarterTrailerSensorProperty;
  rawData?: object[]
}

// units that get returned with sensor readings from the sensor service
export enum SensorUnits {
  PSI = 'Psi',
  KPA = 'kPa',
  F   = 'F',
  C   = 'C',
  V   = 'V'
}

// the types of sensors we can expect to get readings for
export enum SensorTypes {
  DoorState  = 'DoorState',
  TPMS       = 'TPMS',
  WETS       = 'WETS',
  CargoSense = 'CargoSense',
  BMS        = 'BMS',
  WMS        = 'WMS',
  LMS        = 'LMS'
}

// the summary status for Light Out Detection
export enum CircuitSummary {
  GOOD         = 'GOOD',
  FAULT        = 'FAULT',
  UNKNOWN      = 'UNKNOWN'
}

// the values for all Light Out Detection circuits
export enum CircuitStatus {
  GOOD         = 'GOOD',
  RECONNECTED  = 'RECONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  LOW_CURRENT  = 'LOW_CURRENT',
  HIGH_CURRENT = 'HIGH_CURRENT',
  INTERMITTENT = 'INTERMITTENT',
  UNKNOWN      = 'UNKNOWN'
}

export const faultTypes: CircuitStatus[] = [
  CircuitStatus.HIGH_CURRENT,
  CircuitStatus.LOW_CURRENT,
  CircuitStatus.INTERMITTENT,
  CircuitStatus.DISCONNECTED
];

// the types of spreaders we support
export enum SpreaderTypes {
  DICKEY_JOHN   = 'DICKEY_JOHN',
  FORCE_AMERICA = 'FORCE_AMERICA',
  CIRUS         = 'CIRUS',
}

// the types of device peripherals supported
export enum DevicePeripheralTypes {
  FORCEAMERICA = 'FORCEAMERICA',
  DICKEYJOHN   = 'DICKEYJOHN',
  CIRRUS       = 'CIRRUS'
}

// the types of spreader search types
export enum DeviceSpreaderSearchTypes {
  CIRRUS       = 'CIRUS',
  DICKEYJOHN   = 'DICKEY_JOHN',
  FORCEAMERICA = 'FORCE_AMERICA'
}

export enum BrakeChamberPosition {
  frontRight = 'frontRight',
  frontLeft = 'frontLeft',
  rearRight = 'rearRight',
  rearLeft = 'rearLeft',
  parkingBrakeStatus = 'parkingBrakeStatus',
  isDragAlert = 'isDragAlert'
}

export enum BrakeChamberStatusType {
  Released = 'released',
  Engaged = 'engaged',
  Warning = 'warning',
  Caution = 'caution',
  Normal = 'normal',
  Drag = 'drag',
  NotAvailable = 'notAvailable'
}

// the types of sensor features we get from the device request
// @see https://synovia.atlassian.net/wiki/spaces/DMTC/pages/10742104168/Smarter+Trailer+DM+Gateway+API+Contract
export enum DeviceFeatureSensorTypes {
  DoorState  = 'DOOR',
  TPMS       = 'TPMS',
  WETS       = 'WETS',
  CargoSense = 'CARGO',
  PSI_TPMS   = 'PSI_TPMS',
  BMS        = 'BMS',
  WMS        = 'WMS',
  LMS        = 'LMS',
}

// the status types that can be used for a number of readings
// to convery simple alert states
export enum TrailerStatus {
  Normal = 'normal',
  Low    = 'low',
  High   = 'high'
}

// door status types
export enum TrailerDoorStatus {
  Open    = 'open',
  Closed  = 'closed',
  Offline = 'offline'
}

// door event types
export enum TrailerDoorEvents {
  Opened  = 'opened',
  Closed  = 'closed'
}

export enum SmarterTrailerIssueTypes {
  lowPressure       = 'lowPressure',
  highPressure      = 'highPressure',
  highTemperature   = 'highTemperature',
  lowPressureAlarm  = 'lowPressureAlarm',
  highPressureAlarm = 'highPressureAlarm',
  temperatureAlarm  = 'temperatureAlarm'
}

// list of sensor properties enumed for when we need to match a property
export enum SmarterTrailerSensorProperties {
  DoorState      = 'doorState',
  BatteryLevel   = 'batteryLevel',
  AccelerometerX = 'accelerometerX',
  AccelerometerZ = 'accelerometerZ',
  BatteryVoltage = 'batteryVoltage',
  FrameId        = 'frameId',
  Pressure       = 'pressure',
  Temperature    = 'temperature',
  WheelPosition  = 'wheelPosition',
}

// Names that we can more easily use to identify a tire when matching by index
export enum VehicleWithTrailerTires {
  VehicleFrontLeft = 'vehicleFrontLeft',
  VehicleFrontRight = 'vehicleFrontRight',
  VehicleBackRow1LeftOutside = 'vehicleBackRow1LeftOutside',
  VehicleBackRow1LeftInside = 'vehicleBackRow1LeftInside',
  VehicleBackRow1RightOutside = 'vehicleBackRow1RightOutside',
  VehicleBackRow1RightInside = 'vehicleBackRow1RightInside',
  VehicleBackRow2LeftOutside = 'vehicleBackRow2LeftOutside',
  VehicleBackRow2LeftInside = 'vehicleBackRow2LeftInside',
  VehicleBackRow2RightOutside = 'vehicleBackRow2RightOutside',
  VehicleBackRow2RightInside = 'vehicleBackRow2RightInside',
  TrailerFrontLeftOutside = 'trailerFrontLeftOutside',
  TrailerFrontLeftInside = 'trailerFrontLeftInside',
  TrailerFrontRightOutside = 'trailerFrontRightOutside',
  TrailerFrontRightInside = 'trailerFrontRightInside',
  TrailerBackLeftOutside = 'trailerBackLeftOutside',
  TrailerBackLeftInside = 'trailerBackLeftInside',
  TrailerBackRightOutside = 'trailerBackRightOutside',
  TrailerBackRightInside = 'trailerBackRightInside'
}

export enum VehicleWithTrailerWheels {
  HubFrontLeft = 'hubFrontLeft',
  HubFrontRight = 'hubFrontRight',
  HubRearLeft = 'hubRearLeft',
  HubRearRight = 'hubRearRight'
}

// TODO: Technical Debt to map HTP and PSI sensor data seperately.
// Shared strings across different indexes to due to API response.
// mapping of the index received by the API to the actual tire/wheel name and location
// @see https://confluence.cloud.calamp.com/pages/viewpage.action?spaceKey=SD&title=App+Messages
export const trailerSensorDataMapping = {
  //HTL sensors mapped from 0-7
  0  : VehicleWithTrailerTires.TrailerFrontLeftInside,
  1  : VehicleWithTrailerTires.TrailerFrontLeftOutside,
  2  : VehicleWithTrailerTires.TrailerFrontRightInside,
  3  : VehicleWithTrailerTires.TrailerFrontRightOutside,
  4  : VehicleWithTrailerTires.TrailerBackLeftInside,
  5  : VehicleWithTrailerTires.TrailerBackLeftOutside,
  6  : VehicleWithTrailerTires.TrailerBackRightInside,
  7  : VehicleWithTrailerTires.TrailerBackRightOutside,
  8  : VehicleWithTrailerWheels.HubFrontLeft,
  9  : VehicleWithTrailerWheels.HubFrontRight,
  10 : VehicleWithTrailerWheels.HubRearLeft,
  11 : VehicleWithTrailerWheels.HubRearRight,
  // PSI sensors mapped from 17-24
  17 : VehicleWithTrailerTires.TrailerFrontLeftOutside,
  18 : VehicleWithTrailerTires.TrailerFrontLeftInside,
  19 : VehicleWithTrailerTires.TrailerFrontRightInside,
  20 : VehicleWithTrailerTires.TrailerFrontRightOutside,
  21 : VehicleWithTrailerTires.TrailerBackLeftOutside,
  22 : VehicleWithTrailerTires.TrailerBackLeftInside,
  23 : VehicleWithTrailerTires.TrailerBackRightInside,
  24 : VehicleWithTrailerTires.TrailerBackRightOutside
};

// DLS shot name display of the tires mapped from the above names
export enum TireAndWheelDisplayMapping {
  vehicleFrontLeft = 'V-FL',
  vehicleFrontRight = 'V-FR',
  vehicleBackRow1LeftOutside = 'V-B1LO',
  vehicleBackRow1LeftInside = 'V-B1LI',
  vehicleBackRow1RightOutside = 'V-B1RO',
  vehicleBackRow1RightInside = 'V-B1RI',
  vehicleBackRow2LeftOutside = 'V-B2LO',
  vehicleBackRow2LeftInside = 'V-B2LI',
  vehicleBackRow2RightOutside = 'V-B2RO',
  vehicleBackRow2RightInside = 'V-B2RI',
  trailerFrontLeftOutside = 'T-LFO',
  trailerFrontLeftInside = 'T-LFI',
  trailerFrontRightOutside = 'T-RFO',
  trailerFrontRightInside = 'T-RFI',
  trailerBackLeftOutside = 'T-LRO',
  trailerBackLeftInside = 'T-LRI',
  trailerBackRightOutside = 'T-RRO',
  trailerBackRightInside = 'T-RRI',
  hubFrontLeft = 'HUB-LF',
  hubFrontRight = 'HUB-RF',
  hubRearLeft = 'HUB-LR',
  hubRearRight = 'HUB-RR',
}

// DLS shot name display of the wheel hub mapped from the above names
export enum WheelHubsDisplayMapping {
  hubFrontLeft = 'HUB-LF',
  hubFrontRight = 'HUB-RF',
  hubRearLeft = 'HUB-LR',
  hubRearRight = 'HUB-RR',
}

export const sensorBatteryThreshold = {
  maximumV : {
    value : 12.2
  },
  minimumV : {
    value : 3.5 // ????? is this right?
  }
};

// tire thresholds for pressure and temperature
export const tireThresholds = {
  TPMSPsi : {
    minimum : 70,
    maximum : 140,
    optimal : 95
  },
  TPMSkPa : {
    minimum : 482.63,
    maximum : 965.27,
    optimal : 655
  },
  TPMSF : {
    minimum : 0,
    maximum : 239,
    optimal : 158
  },
  TPMSC : {
    minimum : 0,
    maximum : 115,
    optimal : 70
  },
  WETSF : {
    minimum : 0,
    maximum : 176,
    optimal : 116
  },
  WETSC : {
    minimum : 0,
    maximum : 80,
    optimal : 53
  }
};

export enum GeofenceEventType {
  Depart = 'DEPART',
  Arrive = 'ARRIVE',
  Both = 'Both (Enter & Exit)',
  Exit = 'Exit',
  Enter = 'Enter',
};

/**
 * Defines the status states for an asynchronous Task, which represent
 * the following:
 *
 * - PENDING: Task has been placed in the task queue.
 * - IN_PROGRESS: Task has been picked up and is being executed.
 * - RETRYING: Task failed to complete and is currently being attempted again.
 * - RETRY_PENDING: Task failed to complete and has been placed in the retry queue.
 * - RETRY_IN_PROGRESS: Task has been picked up from the retry queue and is being re-executed.
 * - FAILED: Tasks failed to complete successfully.
 * - SCHEDULED: Task has been scheduled for an upcoming executing.
 * - COMPLETED: Task has successfully completed.
 * - PARTIAL_SUCCESS: Portions of the Task have completed successfully.
 */
export enum AsyncTaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  RETRYING = 'RETRYING',
  RETRY_PENDING = 'RETRY_PENDING',
  RETRY_IN_PROGRESS = 'RETRY_IN_PROGRESS',
  FAILED = 'FAILED',
  SCHEDULED = 'SCHEDULED',
  COMPLETED = 'COMPLETED',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  SUCCESS = 'SUCCESS',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Defines the AsyncTask type which provides the state for an asynchronous
 * Task executed against realtime services (WebSockets).
 */
export interface AsyncTask  {
  id: string;
  status: AsyncTask;
  sentDate: string;
  taskName: string;
  progress: string;
  createdAt: string;
  lastModifiedAt: string;
}

export interface IonTagReportType extends SCDeviceType {
  tagReport?: {
    address?: {
      street?: string;
      city?: string;
      state?: string;
    }
    deviceDataConverted?: GenericObject;
    deviceEsn?: number | string;
    eventTime?: string;
    reportingDevice?: Link;
  }
};

/**
 * Defines the interface of the SCI Device.
 */
export interface SCDeviceType {
  accumBatteryVoltage?: AccumType;
  accumBatteryState?: AccumType;
  accumBatteryLevel?: AccumType;
  accumLightLevel?: AccumType;
  accumTemperature?: AccumType;
  batteryLevelUnits?: string;
  batteryLevelValue?: string;
  created?: DateType;
  description?: string;
  deviceName?: string;
  deviceType?: string;
  deviceIdType?: string;
  deviceIdValue?: string;
  deviceAirId?: string;
  device?: {
    id?: string;
    title?: string;
    esn?: string;
    idType?: string;
  };
  gps?: {
    latitude?: number;
    longitude?: number;
    satellites?: number;
    computedFixStatus?: boolean
  };
  batteryLevel?: string;
  messageTypeId?: number,
  messageType?: string;
  messageSequenceNumber?: number;
  messageTime?: DateType;
  messageReceivedTime?: DateType;
  tagReports?: TagReportsType;
  ignition?: string | boolean;
  moving?: string | boolean;
  wirelessModem?: GenericObject;
  pipelineRxTime?: DateType;
  reportingDeviceId?: number;
  reportingDeviceStatus?: unknown;
  reportingDeviceName?: unknown;
  isShockTiltAllowed?: boolean;
  stationary: string;
  stationaryState: string;
  stationaryStateChangeTime: string;
  sensors: Array<GenericObject>;
};

/**
 * Defines the Tracking Popup Pros
 */
export type SCDevicePopupProps = {
  view: __esri.MapView;
  selectedGraphic: __esri.Graphic;
  crumbs?: Crumb[];
  isPhone: boolean;
  isFindNearbyPopup?: boolean,
  setSelectedDevice?: Function;
  setBreadCrumbs?: Function;
  setDeviceDetailView?: Function;
  setFindButtonStatus?: Function;
  assetFindNearbyClick?: Function;
  onFindNearyBy?: Function,
  onFindNearbyBreadcrumbBackClick?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindButtonStateChange?: Function,
  findLabel?: FindNearbyType,
  onDeviceReports?: Function;
};

export interface SCIPendingSettings {
  oceanMode?: boolean;
  autoFlightMode?: boolean | string;
  enableIgnitionDetection?: null;
  zMicroDetection?: boolean;
  flashFallback?: boolean | string;
  pressureSensorFlightMode?: boolean;
  userButtonBehaviour?: string;
  userLedState?: string;
  wakeupPeriod?: string | number;
  timerAccuracy?: string | number;
  moveWakeupPeriod?: string | number;
  movementAccuracy?: string;
  immediateMovementReport?: boolean;
  ignitionOnWakeupPeriod?: string;
  ignitionOnAccuracy?: string;
  lightLevelAlert?: boolean;
  minLightLevel?: string | number;
  maxLightLevel?: string | number;
  temperatureAlert?: boolean;
  minTemperature?: string | number;
  maxTemperature?: string | number;
  impactAlert?: boolean;
  maxImpact?: string | number;
  tipAlert?: boolean;
  maxTip?: string | number;
  temperatureLogging?: boolean;
  sensorLoggingPeriod?: string | number;
  syncSensorLog?: boolean;
  syncSensorLogTempLimit?: string | number;
  sensorEventMode?: null;
  enableMovementDetection?: boolean | string;
  alwaysOn?: boolean | string;
  disableGPS?: null;
  disableUnit?: null;
  stationaryReportingDelay?: number;
  zMicroRxPeriod?: string | number;
  zMicroRxTime?: string | number;
  zMicroDeviceOldTime?: string;
  zMicroDeviceMinRSSINew?: string;
  vehicleInput1Alert?: null;
  minVehicleInput1?: string;
  maxVehicleInput1?: string;
  vehicleInput2Alert?: null;
  logFullGps?: boolean;
  logSendOnFullBuffer?: boolean;
  logCollectWifi?: boolean;
  logCollectCell?: boolean;
  loggingPeriod?: string | number;
  movingLogFullGps?: boolean;
  movingLogSendOnFullBuffer?: boolean;
  movingLogCollectWifi?: boolean;
  movingLogCollectCell?: boolean;
  movingLowLatencyReporting?: null;
  movingLoggingPeriod?: string | number;
  ignitionLogFullGps?: null;
  ignitionLogSendOnFullBuffer?: null;
  ignitionLogCollectWifi?: null;
  ignitionLogCollectCell?: null;
  ignitionLoggingPeriod?: string;
  HWServerPhoneNumber?: number;
  moveRepType?: number;
  wifiNetworks?: {
    networks: GenericObject[]
  }
};

export interface SCIField {
  label: string,
  [key: string]: boolean | string | number
}

// SCI device Tags
export enum SCIDeviceTags {
  SCI = 'SCI',
  TAG = 'Tag'
}

// MDT device Tags
export enum MDTDeviceTags {
  CMF = 'CMF'
}

export enum MessageType {
  EVENT_REPORT = 'EVENT_REPORT',
  TAG_REPORT = 'TAG_REPORT',
  LOCO_EVENT = 'LOCO_EVENT'
};

export interface TagReportsType {
  map: (tagReport: GenericObject) => unknown;
  serialNumber?: string;
}

/**
 * Defines corresponding time in seconds for time table used in ReportingRateChooser
 */
export const RateTimeTable = {
  '1m'  : 60,
  '2m'  : 120,
  '3m'  : 180,
  '5m'  : 300,
  '10m' : 600,
  '15m' : 900,
  '30m' : 1800,
  '1h'  : 3600,
  '2h'  : 7200,
  '4h'  : 14400,
  '8h'  : 28800,
  '12h' : 43200,
  '18h' : 64800,
  '24h' : 86400,
  none  : 0
};

/**
 * Defines all SCI Devices type
 */
export enum SCIDeviceType {
  SC1004 = 'SC1004',
  SC1004V = 'SC1004V',
  SC1100 = 'SC1100',
  SC1102 = 'SC1102',
  SC1204 = 'SC1204',
  SC1204S = 'SC1204S',
  SC1204V = 'SC1204V',
  SC1300 = 'SC1300',
  SC1302 = 'SC1302',
  STAG09XT = 'STAG09XT'
}

export enum NonFlightModeType {
  SC1205V = 'SC1205V'
};

/**
 * Defines all SCI Devices Temperature Logging fields
 */
export const SCITemperatureFields = {
  minLightLevel       : 'Minimum light level',
  minTemperature      : 'Minimum temperature (°C)',
  maxLightLevel       : 'Maximum light level',
  maxTemperature      : 'Maximum temperature (°C)',
  maxImpact           : 'Maximum impact (G)',
  maxTip              : 'Maximum tip angle (˚)',
  sensorLoggingPeriod : 'Sensor Logging Period (s)',
  STAG09XT            : 'Thermocouple Logging'
};

// Defines Change Password request body
export interface PasswordChange {
  currentPassword: string,
  newPassword: string
};

export enum BulkSettingFieldValues {
  default = 'NO CHANGE',
  on = 'ON',
  off = 'OFF',
  bulkDefault = '--'
}

// Type of an Account's Subscription
export interface Subscription {
  id?: number;
  name: string;
  description: string;
  lowerSubscriptions?: Link[];
  applicationAttributes?: GenericObject;
  applications?: Link[];
  modules?: Link[];
  version?: number;
  _self?: Link;
};

// Type of an Account's Service Profile
export interface ServiceProfile {
  id?: number;
  name: string;
  spVersion: string;
  description?: string;
  account?: AccountLink;
  version: string;
};

export interface Feature {
  Features: GenericObject;
  DASHBOARD_PAGE: string;
  REPORTS: string;
  modules: RoleFeatureModule[];
  name: string;
  id: string;
  has: (id: string) => boolean;
}

export interface RoleFeatureModule {
  id: string;
  name: string;
  enabled: boolean;
}

// Defines the Accumulator type
export interface AccumulatorType {
  label: string;
  value: string;
  units: string;
  index: string;
  type: string;
}

// Defines the Peg Behavior type
export interface PegBehaviorType {
  href: string;
  status: string;
  title: string;
  source: {
    href: string;
    rel: string;
    title: string;
  }
}

// Defines the Status type
export interface StatusType {
  charging: boolean;
  externalPower: boolean;
  lastUpdate: string;
  seqNum: number;
  signal?: number;
  tamper?: number
  vehicleInput1?: number | boolean;
  vehicleInput2?: number | boolean;
}

// Defines the States type
export interface StatesType {
  ignition: boolean;
  moving: boolean;
  tamper: boolean;
}

// Defines the iOn Tag reports type
export interface IonTagReportsType {
  tagReport: {
    account: any;
    address: any;
    batchId: string;
    cep: number;
    device: any;
    deviceDataConverted: {
      accumulators: AccumulatorType[];
      pegBehavior: PegBehaviorType;
      rssi: RssiType;
      systemOfUnits: string;
      type: string;
    }
    deviceId: string;
    eventTime: string;
    fixStatus: boolean;
    latitude: number;
    longitude: number;
    messageReceivedTime: string;
    messageUuid: string;
    model: string;
    reportingDevice: any;
    status: StatusType;
    type: number;
  }
}

// Defined the SC Device reports type
export interface ScReportsType {
  locoEvent: {
    account: any;
    address: {
      addressLatitude: number;
      addressLongitude: number;
      city: string;
      country: string;
      county: string;
      crossStreet: string;
      distance: number;
      houseNumber: string;
      label: string;
      postalCode: string;
      postedSpeedLimit: number;
      roadTypeCode: string;
      speedLimitUnits: string;
      state: string;
      stateProvinceCd: string;
      street: string;
      timeZoneId: string;
      timeZoneOffset: string;
      tollRoadFlag: string;
    };
    batchId: string;
    cep: number;
    created: string;
    device: {
      href: string;
      rel: string;
      status: string;
      title: string;
    };
    deviceAirId: string;
    deviceDataConverted: {
      accumulators: AccumulatorType[];
      pegBehavior: PegBehaviorType;
      rssi: RssiType;
      systemOfUnits: string;
      type: string;
    };
    deviceEsn: string;
    deviceId: string;
    deviceMessageSequenceNumber: number;
    eventTime: string;
    event?: {
      type?: string;
      g?: number;
      angle?: number;
    };
    fixStatus: boolean;
    latitude: number;
    lmdirectMessageType: number;
    longitude: number;
    messageReceivedTime: string;
    messageUuid: string;
    model: string;
    nextUpdateTime: string;
    processedTagReports: boolean;
    slaveList: string[];
    status: StatusType;
    states: StatesType;
    type: number;
    wifi: {mac: number; rssi: number; ssid?: string}[];
  }
};

// defines the sensor types enumeration ...
export enum SensorType {
  Temperature = 'temperature',
  Light       = 'lightLevel',
  Shock       = 'shock',
  Tilt        = 'tilt',
  Tilted      = 'tilted',
  Humidity    = 'humidity'
};

// Defines the iOn Tag sensor types to display
export const SC_TAG_TYPES = {
  STAG04HT : [SensorType.Temperature, SensorType.Humidity],
  STAG09XT : [SensorType.Temperature]
};

// Defines the SC Device sensor types to display
const ALL_SENSOR_TYPES_ENABLED = [
  SensorType.Temperature,
  SensorType.Light,
  SensorType.Shock,
  SensorType.Tilt
];

// Defines the SC Device sensor types that supports shock and tilt...
export const SHOCK_TILT_ALLOWED_DEVICES = [
  SCIDeviceType.SC1004,
  SCIDeviceType.SC1004V,
  SCIDeviceType.SC1204,
  NonFlightModeType.SC1205V,
  SCIDeviceType.SC1204S,
  SCIDeviceType.SC1204V
];

// Defines which SC Device sensors a given device should display
export const SC_DEVICE_TYPES = {
  SC1004  : ALL_SENSOR_TYPES_ENABLED,
  SC1004V : ALL_SENSOR_TYPES_ENABLED,
  SC1204  : ALL_SENSOR_TYPES_ENABLED,
  SC1204V : ALL_SENSOR_TYPES_ENABLED,
  SC1204S : ALL_SENSOR_TYPES_ENABLED,
  SC1205V : ALL_SENSOR_TYPES_ENABLED,
  SC1102  : [SensorType.Temperature],
  SC1302  : [SensorType.Temperature, SensorType.Light]
};

// Defines the useDeviceSensor message types
export enum MESSAGE_TYPES {
  LOCO_MESSAGE = 'LOCO_MESSAGE',
  TAG_REPORT = 'TAG_REPORT'
};

// defines the impact types ...
export enum ImpactType {
  Drop = 'drop',
  Impact = 'impact'
};

// Device Sensor types
export enum DeviceSensorStatus {
  Normal = 'normal',
  Alert = 'alert',
};

export enum DownloadFileTypes {
  CSV = 'csv',
  XML = 'xml'
}

export enum SPREADER_SENSOR_TYPES {
  DICKEY_JOHN = 'DICKEY_JOHN',
  FORCE_AMERICA = 'FORCE_AMERICA'
}

export const spreaderProperties = {
  [SPREADER_SENSOR_TYPES.DICKEY_JOHN] : [
    'GRANULAR_1',
    'GRANULAR_2',
    'LIQUID_1',
    'LIQUID_2',
    'VEHICLE_DISTANCE'
  ]
};

export const DICKEY_JOHN_CHANNEL_ATTRIBUTES = [
  'targetApplicationRate',
  'actualApplicationRate'
];

export const dickeyJohnPrimaryProperties = {
  mss                   : 'mss',
  roadTemperatureValue  : 'roadTemperature',
  tankLevelValue        : 'tankLevel',
  gateHeightValue       : 'gateHeight',
  targetApplicationRate : 'targetApplicationRate',
  actualApplicationRate : 'actualApplicationRate',
  groundSpeed           : 'groundSpeed'
};

export const dickeyJohnSensorProperties = [
  'sensorSetting',
  'sensorUnits',
  'sensorType',
  'sensorValue'
];

export const forceAmericaSecondaryProperties = {
  airTemperature      : 'airTemperature',
  roadTemperature     : 'roadTemperature',
  spinnerDialPosition : 'spinnerDialPosition',
  time                : 'time',
  date                : 'date'
};

export const forceAmericaPrimaryProperties = {
  spreaderStatus        : 'spreaderStatus',
  granularMaterialName  : 'granularMaterialName',
  granularSetRate       : 'granularSetRate',
  granularRateIndex     : 'granularRateIndex',
  granularMaterialTotal : 'granularMaterialTotal',
  prewetVolumeTotal     : 'prewetVolumeTotal',
  directLiquidTotal     : 'directLiquidTotal',
  dustTotal             : 'dustTotal',
  granularLoopMode      : 'granularLoopMode'
};
export const forceAmericaDisplayProperties = {
  ...forceAmericaPrimaryProperties,
  ...forceAmericaSecondaryProperties
};

export const cirusPrimaryProperties = {
  dryDistance     : 'dryDistance',
  vehicleDistance : 'vehicleDistance',
  granular        : 'granular',
  prewetSetting   : 'prewetSetting',
  antiiceSetting  : 'antiiceSetting'
};

export enum BulkActionEntity {
  DEVICES = Entity.device,
  ASSETS  = Entity.asset,
  ROLES   = Entity.role,
  USERS   = Entity.user,
  PROVISIONEDDEVICE = Entity.provisionedDevice,
  INVENTORY = Entity.inventorydevice,
  MANUFACTURED_DEVICES = Entity.manufacturedDevice,
  FILE_META = Entity.filemeta
};

export interface BulkEntityOperation extends OperationDetails {
  entityIdentifierType: string;
  hierLevel?: HierLevel;
  searchCriteria?: APIGatewaySmartSearch | APIGatewayMemberSearch;
  entityIdentifierList?: string[];
  entityIdentifierS3Path?: string;
  solomonId?: string;
  currRegion?: string;
  search?: GenericObject;
  isProvisioning?: boolean;
}

export interface TaskEmailExportOperation {
  taskId: string;
  emailRecipients: Object;
  exportColumns: Object;
  customLogicFields: Object;
  dateFormatFields: Object;
  timeZone: string;
};

export enum BulkActionType {
  MOVE = 'move',
  GROUP_ASSIGNMENT = 'assignGroup',
  JOB_CREATION = 'createJob',
  SCHEDULE_DEVICE_SYNC = 'scheduleDeviceSync',
  SERVICE_ENABLEMENT = 'serviceEnablement',
  UNPAIR = 'unpair',
  SCI_UPDATE = 'sciUpdate',
  BULK_EDIT = 'bulkEdit',
  COMMON_FIELD_UPDATE = 'commonFieldUpdate',
  BULK_ADD = 'bulkAdd',
  COMMON_FIELD_CREATE = 'commonFieldCreate',
  VBUS = 'vBusStatus',
  ASSIGN_REGION = 'assignRegion',
  CREATE_MFG_DEVICES = 'createMfgDevices',
  VEHICLE_PROFILE = 'vehicleTestFlag',
  DEVICE_PASSWORD = 'devicePasswordExport',
  ENABLESERVICES = 'enableServices',
}

export interface OperationDetails {
  accountId: number;
  bulkOperation: BulkOperation;
  entityType: BulkActionEntity;
  taskNickName: string;
}

export interface ManufacturedDeviceDetails extends OperationDetails {
  entityIdentifierS3Path?: string;
}

export interface OperationTask {
  accountId: number;
  createdAt: number;
  id: string;
  name: string;
  others: OperationDetails;
  status: AsyncTaskStatus;
  userId: number;
  username: string;
}

export type ActionDetails = GenericObject;

export interface BulkOperation {
  actionType: BulkActionType;
  actionDetails?: ActionDetails;
  action?: boolean;
  emailRecipients?: Array<[]>;
}

export interface KeyFileSecurityEnable {
  enableFileAuthentication : string;
  disableSms : string;
  enableLMDirectServicesPayloadEncryption : string;
  disableLMDirectForwarding : string;
  disableApplicationFirmwareDowngrade : string;
  enableLmDirectInboundMessageAuthentication : string;
  disableWebBrowser : string;
  enableSmsAuthentication : string;
  enableSmsPassword : string;
  enableSequenceNumberEnforcement : string;
  enableLmDirectOutboundMessageAuthentication : string;
  enableAtCommandPassword : string;
  enableReadProtection : string;
  enableLmDirectSendPayloadEncryption : string;
};
export type KeyFileSecurityEnables = KeyFileSecurityEnable[];

export interface KeyfileBuilderInterface {
  lmDirectMessageAuthenticationSeedIndexes: {
    primaryInterface: string;
    secondaryInterface: string;
    maintenanceForwardingInterface: string;
    inbound: string;
  };
  smsMessageAuthenticationSeedIndex: {
    sms: string;
  };
  lmDirectPayloadEncryption: {
    configuration: string;
    uniqueKeySelection: string;
    secretKey: string;
  };
  passwords: {
    lmDirectAuthentication: string;
    atCommandPassword: string;
    smsPassword: string;
  };
  securityEnables: Array<KeyFileSecurityEnable>;
  customerFileDigitalSignatureKey: string;
  encryptionKey: string;
}
export type Keyfiles = KeyfileBuilderInterface[];

export interface CRUDPermissions extends CTCEntity {
  hasAddPermission?: boolean;
  hasReadPermission?: boolean;
  hasUpdatePermission?: boolean;
  hasDeletePermission?: boolean;
}

// Defines the TimeSeries type
export interface TimeSeriesType {
  label?: string;
  time?: string;
  units: string;
  index: string;
  type: string;
  value: string;
}

// Defines the Generic Sensor peripheral attachment types
export const enum GenericSensorAttachmentTypes {
  GENERICSENSOR = 'GENERICSENSOR',
}

export interface UpdateAssetAction {
  isError?: boolean;
  showTreeRefresh?: boolean;
}

export interface InventoryDevice {
  id: number
  deviceId: string | number;
  deviceTypeName: string;
  currRegion: string;
  serialNumber?: string;
  solomonId?: string;
  currRegionCreatedOn?: Date | string;
  currRegionCreatedBy?: Date | string;
  createdOn?: Date | string;
  status?: EntityStatus;
  partNum?: string;
  custScriptVer?: string;
  firmwareVersion?: string;
  shipDate?: Date | string;
  externalData?: {
    shipDate?: Date | string;
    fileVersions: string;
    purchaseOrder: string;
    salesOrder: string;
    trackingNumber: string;
  };
};
export type InventoryDevices = InventoryDevice[];

export interface ApplicationId {
  id: number
  applicationId: string;
  applicationIdType: string;
  manufacturer: string;
  model: string;
  microprocessor?: string;
  cellularModem?: string;
  deviceFamily?: string;
  carrier?: string;
  createdOn?: Date | string;
  lastModifiedOn?: Date | string;
  status?: string;
};

export type ApplicationIds = ApplicationId[];

export interface Shipment {
  id: number
  trackingNum: string;
  shipperId: string;
  salesOrderNum: string;
  purchaseOrderNum?: string;
  ctcRegion?: string;
  shipDate?: Date | string;
  createdDate?: Date | string;
  lastModifiedOn?: Date | string;
};

export type Shipments = Shipment[];

export interface DevicePartNumber extends CTCEntity {
  devicePartNum: number;
  description?: string;
  deviceType: string;
  deviceConfigurationName: string;
  carrier?: string;
  isThirdParty?: boolean;
  id?: number;
};

export type DevicePartNumbers = DevicePartNumber[]

export interface ManufacturingCarrier extends CTCEntities {
  name: string;
  status: string;
  type: string;
};

export type ManufacturingCarriers = ManufacturingCarrier[];

export interface ManufacturedDevice {
  id: number;
  deviceFamily: string;
  primaryDeviceIdentifier?: string;
  mfgStartedOn?: string;
  mfgElapsedTimesec?: string;
  testRunStatus?: string;
  mfgLocation?: string;
  devicePartNum?: string;
  testRunIdent?: string;
  isDmDevice?: string;
  applicationId?: string;
  secondaryIdents?: string;
  otherProperties?: string;
  fileVersions?: string;
  status?: string;
  pcbaNumber?: string;
  createdOn?: Date | string;
  firmwareVersion?: string
};

export type ManufacturedDevices = ManufacturedDevice[];

export interface ManufacturedDeviceFilters {
  secondaryIdents?: string | null;
};

export enum ValidVisionRequestProperties {
  Architecture = 'LightMetrics',
  Category = 'CAMERA',
};

export enum VideoResolution {
  Low = '320x180',
  Medium = '640x360',
  High = '1280x720',
};

export enum VideoQuality {
  Low = 1,
  Medium = 5,
  High = 10
};

export enum TimeLapseOption {
  CaptureInterval = 1,  // Capture one frame every 1 second
  DisplayInterval = 0.1, // Display each frame for 0.1 seconds
}

export enum VideoDuration {
  ONE_MINUTE = 1,
  TWO_MINUTES = 2,
  THREE_MINUTES = 3,
  FIVE_MINUTES = 5
}

export enum DvrRequestField {
  AssetName = 'assetName',
  Date = 'date',
  Time = 'time',
  Duration = 'duration',
  Quality = 'quality'
};

export enum DvrRequestStatus {
  unavailable = 'unavailable',
  finished = 'finished',
  canceled = 'canceled',
  pending = 'pending',
  failed = 'failed'
};

export interface SFTPConnection {
  serverName: string;
  port: number | string;
  host: string;
  username: string;
  password: string;
  privateKey: string;
  allowedAccounts: number[];
};

export interface CameraHistoryEvent {
  deviceCategory?: string;
  isCameraOnly?: boolean;
}

export enum UnitOfMeasure {
  US = 'US',
  Metric = 'Metric',
  Imperial = 'Imperial'
}

export interface AccountInfo {
  account: {
    status: string;
    applications: Link[];
  };
}

export enum ErrorCode {
  Unauthorized = 401,
  Forbidden = 403,
  MethodNotAllowed = 405
}

export enum JobConflictResolution {
  CANCEL = 'CANCEL',
  REMOVE = 'REMOVE',
  IGNORE = 'IGNORE',
}

export enum JobConflictStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  THROTTLED = 'THROTTLED',
  RETRY = 'RETRY'
}
